// Portfolio Module - Holdings management, XIRR calculations, bulk operations
// Extracted from index.html lines 1226-1240, 1206-1224, 2315-2320, 3261-3346, 3348-3353, 3394-3397, 3398-3403, 3450-3493, 3495, 3498-3510

import { S, save, stampItem, tombstoneId, softDelete } from '../core/state.js';
import { hVal, hPnl, hTotUSD, hWealthUSD, hTotLocal, hMoInvUSD, xirr, getMs, tdAccrued, lotSummary, lotAvgCost, blendedAvgCost, holdingAfterExit, averagedTranche } from '../core/math.js';
import { esc, f, fU, fT, fP, fByCur, pc, bc, uid, now, toUSD, toUSDAt, VALID_CURRENCIES, dividendUSD, settlementCurrency, isPassiveDividend } from '../core/utils.js';
import { priceSale, pricePurchase } from '../core/fees.js';
import { fundsFromCashLedger } from '../core/portfolio-policy.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm, uiPrompt } from '../ui/dialog.js';
import { _safeChart, renderAllocCh } from '../ui/charts.js';
import { PS } from '../core/prices.js';
import { driftHoldings, sleeveHasTargets } from '../core/risk-calc.js';
import { monthLots, lotEntry } from '../core/month-lots.js';
import { lg } from '../modules/history.js';
import { markDirty, renderAll } from '../app/render-loop.js';
import { holdingDividends } from '../modules/dividends.js';
import { holdingSignalStance } from '../modules/signals.js';
import { stanceCard, stanceOrder } from '../ui/stance-card.js';

// Globals
let sortC = null, sortD = 1, editHId = null;

// Build XIRR cashflows from portfolio monthly investments + current value
// Supports both month-based holdings and lot-based holdings (SAP etc.)
// Basis-symmetry rule (audit 2026-07-31): every dollar of capital whose value
// enters the final inflow must appear as a dated outflow. Exceptions by design:
// employer match + DRIP lots are free return (decision 2026-07-21); Cash-type
// holdings have no return concept and stay out of BOTH sides.
/**
 * THE OUTFLOWS FOR A HOLDING THAT HAS LOTS — INCLUDING THE PART THE LOTS DO NOT DESCRIBE.
 *
 * RET-003, raised 2026-08-10 as `DATA-004`'s neighbour and measured 2026-08-15. Both XIRR
 * builders took `h.lots` as the whole basis and returned. `hTotUSD` has said the opposite since
 * 2026-08-03 — *"`qty * avgCost` FIRST, lots second … lots become additive provenance rather
 * than an all-or-nothing basis"* — and RET-001 (`4002a66`) fixed exactly this shape in
 * `holdingAfterExit`. It was never swept into the two functions here. Rule #25.
 *
 * Measured on the live book: six holdings whose lots cover part of the position.
 *
 *   ENJSA  208 of 388.68 sh   $495 of $879      THYAO   24 of 69.13 sh    $159 of $436
 *   TUPRS    9 of 138.53 sh    $60 of $654      XAG   76.9 of 348.53      $155 of $776
 *   TEJ  7,539 of 25,665.74   $222 of $1,003    NVB  5,445 of 9,313.95    $540 of $877
 *
 * **$2,994 — 7.7 % of the $39,020 non-cash basis — never appeared as an outflow**, while the
 * full position value counted in the terminal inflow. One-directional: it flatters the return.
 * Portfolio XIRR read **−7.02 %** against a true **−23.9 %**, and TEJ alone reported **+341.53 %**.
 * (`project_goals_derived_audit` records a ~294 % XIRR already found fabricated once, in a
 * different function, and fixed as `042a279`. Same disease, another host.)
 *
 * ── THE DATE IS A DECISION, AND IT WAS HIS ─────────────────────────────────────────────────
 *
 * The un-lotted basis is an averaged legacy position: the book does not record when it was
 * bought. The portfolio number barely cares (−23.89 % dating it at the earliest lot vs −23.86 %
 * at tracking start), but the PER-HOLDING number swings 20–90 points on that choice — ENJSA
 * −70.2 % vs −96.3 %, NVB null vs +80.5 %. Put to the owner with both columns on 2026-08-15.
 *
 * **His decision: one rule everywhere — the remainder is dated at tracking start.**
 *
 * `h.since` is preferred when the holding declares one, because that is a recorded fact about
 * the holding rather than a fallback. **No live holding of the six carries it**, so today this
 * is exactly the tracking-start rule he chose; the branch exists so a holding that DOES record
 * its start is not overridden by a global default.
 */
export function lotOutflows(h){
  const out=[];
  let lotLocal=0;
  (h.lots||[]).filter(l=>l&&l.type!=='drip').forEach(l=>{
    const costLocal=Number(l.cost)||(Number(l.qty)||0)*(Number(l.price)||0);
    const costUSD=toUSDAt(costLocal,h.currency,l.date);
    if(costUSD>0){
      out.push({date:new Date(String(l.date).slice(0,10)+'T12:00:00'),amount:-costUSD});
      lotLocal+=costLocal;
    }
  });
  // ⚠ THE REMAINDER IS SUBTRACTED IN LOCAL CURRENCY AND CONVERTED AT ITS OWN DATE.
  //
  // The first version of this did `hTotUSD(h) - lotUSD`, which is arithmetic between two
  // different dollars: `hTotUSD` converts the whole basis at TODAY's rate, while the lot costs
  // were converted at their own dates. The remainder then carried today's FX on a flow dated
  // months earlier — inconsistent with both other branches of `xirrCashflows`, which use
  // `toUSDAt(..., l.date)` and `toUSDAt(..., m)`.
  //
  // Measured after the first deploy: it read −22.1 % where the local-currency computation gives
  // −23.9 %. **1.8 points, entirely from converting January lira at August's rate** (35 → 40).
  // `hTotLocal` carries the same basis rule as `hTotUSD` — one definition, not a restatement.
  const gapLocal=(hTotLocal(h)||0)-lotLocal;
  if(gapLocal>0){
    const m0=h.since?String(h.since).slice(0,7)
      :String((S.goalSettings&&S.goalSettings.investingSince)||getMs()[0]||'').slice(0,7);
    if(m0){
      const gapUSD=toUSDAt(gapLocal,h.currency,m0+'-28');
      if(gapUSD>0.01)out.push({date:new Date(m0+'-28T12:00:00'),amount:-gapUSD});
    }
  }
  // The remainder is appended after the lots and is usually OLDER than all of them, so this
  // list leaves here out of order unless it is sorted. `xirr` now sorts defensively too
  // (RET-005) — this is the belt, that is the braces, and neither is redundant: a caller that
  // slices or merges these flows should receive them in chronological order.
  return out.sort((a,b)=>a.date-b.date);
}

export function xirrCashflows(){
  const ms=getMs();
  const cfs=[];
  S.holdings.forEach(h=>{
    if(h.type==='Cash')return;
    // ⚠ A CLOSED POSITION HAS NO LEGS IN THIS SERIES — RET-002, measured 2026-08-16.
    //
    // The `basis <= 0` guard below cannot catch an exited holding. With `qty = 0` and its lots
    // consumed, `hTotUSD` falls past both of its own guards to its last resort — the monthly-plan
    // history — and answers with what the holding cost over its WHOLE LIFE. So `basis > 0`, the
    // guard never fires, and outflows are emitted. The terminal inflow, meanwhile, is zero for
    // that holding by construction. One leg, no inverse (rule #26).
    //
    // Measured on the live book: 15 exited non-cash holdings, 13 with plan history,
    // USD 5,167.93 plus a TRY leg worth USD 1,695.50 — about USD 6,863 of money never spent,
    // counted as spent, against a non-cash basis near USD 39,020. That USD figure is the same
    // 5,167.93 `hWealthUSD`'s docblock records for the eight IBKR positions exited on
    // 2026-08-06: the same fallback through a second door, closed in net worth on 2026-08-10
    // and never closed here.
    //
    // ⚠ THE REGISTER NAMED THE LOT BRANCH AND THE LOT BRANCH IS FINE: a full exit consumes lots
    // FIFO and drops the emptied ones (`h.lots = h.lots.filter(l => l.qty > 0.0001)`), so an
    // exited holding has none. Live: 16 holdings with lots, 15 exited, intersection empty. This
    // guard sits above BOTH branches, so it closes the register's version by construction.
    //
    // HIS DECISION, 2026-08-16 — option (A): this series is the return of the book he holds now.
    // An exited position drops out on BOTH legs, which is what the code already did for the two
    // exited holdings that have no plan history, and the rule `hWealthUSD` established for net
    // worth. Option (B) — every position ever held, with `S.realized` proceeds as dated inflows —
    // is the truer money-weighted number and is its own piece of work, not a rider on this one.
    if(!(h.qty>0))return;
    // Lot-based outflows (investment lots only - DRIP/match is free return), PLUS the basis the
    // lot list does not describe. See lotOutflows.
    if(h.lots&&h.lots.length){
      lotOutflows(h).forEach(cf=>cfs.push(cf));
      return;
    }
    const basis=hTotUSD(h);
    if(basis<=0)return;
    // Dated candidates: reconciled per-ticker entries (actual buys) first,
    // plan-derived amounts only for months without entries.
    const cand=[];
    ms.forEach(m=>{
      const r=S.monthlyRates[m];
      if(!r)return;
      if(r.entries&&r.entries[h.ticker]){
        const e=r.entries[h.ticker];
        const usd=toUSDAt(e.qty*e.price,h.currency,m);
        if(usd>0)cand.push({m,usd});
      }else if(!r.entries&&r.usdtry>0&&(h.allocationPct||0)>0&&(!h.since||m>=h.since)){
        const usd=S.base*(r.mul||1)*((h.allocationPct||0)/100);
        if(usd>0)cand.push({m,usd});
      }
    });
    const candSum=cand.reduce((s,c)=>s+c.usd,0);
    if(candSum>0){
      // Scale so dated flows sum exactly to the holding's real cost basis.
      const k=basis/candSum;
      cand.forEach(c=>cfs.push({date:new Date(c.m+'-28T12:00:00'),amount:-c.usd*k}));
    }else{
      // No dated record anywhere (pre-app positions): opening flow at tracking
      // start. Never fabricates pre-milat history.
      const m0=String(S.goalSettings?.investingSince||ms[0]||'').slice(0,7);
      if(m0)cfs.push({date:new Date(m0+'-28T12:00:00'),amount:-basis});
    }
  });
  if(!cfs.length)return null;
  // Current non-cash portfolio value as final inflow
  // ⚠ THE INFLOW SIDE USES `hWealthUSD`, NOT `hVal` — RET-004.
  //
  // `hVal` returns nothing for TWO unrelated reasons: no position, and no price. This line read
  // `v.vU` and so valued an UNPRICED holding at zero while its basis stayed in the outflows —
  // the aggregate cannot balance that way. `hWealthUSD` is the function that separates the two:
  // a closed position is worth nothing, an unpriced one is worth what was paid, and it says
  // which with `atCost`. The `qty > 0` guard above already removes the closed ones, so what this
  // change adds is the unpriced case. Live exposure when measured: ZERO — all 42 non-cash
  // holdings with a position carry a price — so this is the trigger being disarmed, not a number
  // moving.
  //
  // ⚠ DELIBERATE ASYMMETRY WITH `calcHoldingXIRR`, which keeps `hVal` and returns null. A single
  // holding priced at cost would report a 0 % return we do not know to be true; an empty cell is
  // the honest answer there. An AGGREGATE has no such option — dropping one holding's value
  // while keeping its cost misstates every other holding's contribution. Same question, two
  // right answers, and a test pins both so a later sweep does not "finish" the job.
  let curVal=0;
  S.holdings.forEach(h=>{if(h.type==='Cash')return;const w=hWealthUSD(h);if(w.usd>0)curVal+=w.usd;});
  if(curVal<=0)return null;
  cfs.push({date:new Date(),amount:curVal});
  cfs.sort((a,b)=>a.date-b.date);
  return cfs;
}
export function calcPortfolioXIRR(){
  const cfs=xirrCashflows();
  return cfs?xirr(cfs):null;
}

// XIRR for individual holding — supports lot-based and month-based
export function calcHoldingXIRR(h){
  const cfs=[];
  if(h.lots&&h.lots.length){
    // ONE definition, both callers (rule #24). This function had its own copy of the lot loop,
    // which is how it kept the RET-003 asymmetry after `hTotUSD` and `holdingAfterExit` had
    // both been corrected — and it is the column that reported TEJ at +341.53 %.
    lotOutflows(h).forEach(cf=>cfs.push(cf));
  }else{
    const ms=getMs();if(!ms.length)return null;
    ms.forEach(m=>{
      const amtUSD=hMoInvUSD(h,m);
      if(amtUSD<=0)return;
      cfs.push({date:new Date(m+'-28T12:00:00'),amount:-amtUSD});
    });
  }
  if(!cfs.length)return null;
  const v=hVal(h);if(!v.vU||v.vU<=0)return null;
  cfs.push({date:new Date(),amount:v.vU});
  return xirr(cfs);
}

// Sort function for portfolio table
export function srt(c){if(sortC===c)sortD*=-1;else{sortC=c;sortD=1;}renderP();updateSortArrows();}

function updateSortArrows(){
  document.querySelectorAll('th[data-action="srt"]').forEach(th=>{
    // Remove existing arrow span
    const ex=th.querySelector('.sort-arrow');if(ex)ex.remove();
    const col=JSON.parse(th.dataset.args||'[]')[0];
    if(col===sortC){
      const arrow=document.createElement('span');
      arrow.className='sort-arrow';
      arrow.textContent=sortD===1?' ▲':' ▼';
      arrow.style.cssText='font-size:9px;opacity:.6;margin-left:2px';
      th.appendChild(arrow);
    }
  });
}

// Main holdings table renderer
/**
 * THE DETAIL ROW UNDER A HOLDING — basis summary, Exit Holding, + Lot.
 *
 * It rendered only when `lotSummary(h)` returned something, i.e. only for a holding that
 * carries a lot list. TWO of forty-four do. So `Exit Holding` — repaired the same day to work
 * without lots — was still unreachable for the other forty-two, because the markup carrying
 * it was never emitted. The owner found it immediately: "exit holding sadece tej üzerinde
 * görünüyor, ben nasıl çıkacağım?"
 *
 * Handler fixed, control still invisible. That is the 2026-08-03 "a control can be dead in
 * TWO layers" finding, walked into again the next day — and the reason the rule now says to
 * check the RENDER PATH, not only the stylesheet, before calling a control fixed.
 *
 * One definition, two callers (renderP's table and the neon table), so the next change to
 * this row cannot land in one of them and miss the other. With lots it shows the
 * investment/DRIP split; without, the averaged basis the holding actually has.
 * (audit 2026-08-04)
 */
function _holdingDetailRow(h, ls, o) {
  const hasPos = (h.qty || 0) > 0;
  const ls2 = lotSummary(h);
  // Branch on STATUS, never on whether the row exists. This used to `return ''` when a holding
  // had neither lots nor a position — which is precisely the state EVERY planned buy sits in.
  // `+ Lot` below is the only caller of openAddLot in the app, so 19 of 44 holdings had no way
  // to record a first purchase at all, and a holding you exited could never be re-entered.
  // Same shape as the Exit Holding finding in dd14bc3, one layer further out. (2026-08-06)
  const basis = ls2
    ? `<span style="color:${o.acc}">Investment:</span> ${f(ls2.investedQty, 4)} · ${ls}${f(ls2.investedCost)}`
      + `<span style="margin-left:8px;color:${o.grn}">DRIP:</span> ${f(ls2.dripQty, 4)} · ${ls}${f(ls2.dripCost)}`
      + `<span style="margin-left:8px;color:var(--t2)">${ls2.lotCount} lots</span>`
    : hasPos
      ? `<span style="color:${o.acc}">Held:</span> ${f(h.qty || 0, 4)} · avg ${ls}${f(h.avgCost || 0, 4)}`
        + `<span style="margin-left:8px;color:var(--t2)">${ls}${f((h.qty || 0) * (h.avgCost || 0))} at cost · averaged, no lots</span>`
      : `<span style="color:var(--t3)">No position${(h.allocationPct || 0) > 0 ? ` · target ${f(h.allocationPct, 2)}%` : ''}</span>`;
  return `<tr ${o.trAttrs}><td colspan="${o.colspan}" style="${o.tdStyle}">
      <div style="display:flex;gap:16px;font-size:10px;color:var(--t3);align-items:center">
        ${basis}
        ${hasPos ? `<button class="${o.btn}" style="${o.btnStyle};margin-left:auto" data-action="openExitH" data-args='["${h.id}"]'>Exit Holding</button>` : '<span style="margin-left:auto"></span>'}
        ${ls2 ? `<button class="${o.btn}" style="${o.btnStyle}" data-action="openLots" data-args='["${h.id}"]'>Lots</button>` : ''}
        <button class="${o.btn}" style="${o.btnStyle}" data-action="openAddLot" data-args='["${h.id}"]'>+ Lot</button>
      </div></td></tr>`;
}

/**
 * HOLDINGS STANCE — "What the tape says", above the Holdings table.
 *
 * The larger of the two surfaces. Measured 2026-08-16: **8 of his 44 holding tickers carry a
 * signal** (against 2 of 8 trades), and the strongest conflict in the book is here rather than on
 * Trades — **TUPRS**, whose latest call is a SELL and which carries **two unresolved TRIGGERED
 * SELLs** on top of it.
 *
 * ⚠ THE SURFACE WAS CHECKED BEFORE THE MOCKUP WAS DRAWN THIS TIME (rule #52). Holdings is
 * `<table id="pnTbl">` with filters and no expandable rows, exactly like Active Trades, so this is
 * its own panel again — placed above the table, where Rebalancing drift already answers "what
 * needs attention". Every column and filter of the table is untouched.
 *
 * ⚠ ONLY `CONFLICTED` IS OPEN AT REST. Trades had two cards and both fitted; here there are
 * eight, and a panel that opens with eight cards is a list rather than a warning. The rest sit
 * behind the page's own `jrn-more` expander with the breakdown in its summary. And the **36
 * holdings with no signal get nothing at all** — no row, no grey chip; an absence rendered as a
 * measurement is the same failure as back-filling a default (rule #28).
 *
 * ⚠ ONE TICKER, ONE CARD. Grouped by `normTicker`, so a second lot in a name he already holds does
 * not draw the same conflict twice. His book is 44 holdings across 44 distinct tickers today, so
 * this changes nothing — it is written for the day it does.
 *
 * Read-only by construction: the shared `stanceCard` renders no control, and tests on both
 * surfaces assert the absence.
 */
function _renderHoldingStance(rows) {
  const $ = id => document.getElementById(id);
  const host = $('pnStance'), panel = $('pnStancePanel'), nEl = $('pnStanceN');
  const moreD = $('pnStanceMoreD'), more = $('pnStanceMore'), moreN = $('pnStanceMoreN');
  if (!host || !panel) return;

  const seen = new Set();
  const cards = [];
  (rows || []).forEach(r => {
    const h = r && r.h;
    if (!h || !h.ticker) return;
    const key = String(h.ticker).trim().toUpperCase().replace(/\.IS$/, '');
    if (seen.has(key)) return;
    seen.add(key);
    let st = null;
    try { st = holdingSignalStance(h); } catch (e) { console.warn('[ZAGX] holding stance failed for', h.ticker, e); }
    if (!st) return;
    cards.push({ h, st });
  });

  if (!cards.length) {
    panel.style.display = 'none';
    host.innerHTML = '';
    if (more) more.innerHTML = '';
    if (moreD) moreD.style.display = 'none';
    return;
  }
  panel.style.display = '';
  cards.sort((a, b) => stanceOrder({ st: a.st, ticker: a.h.ticker }, { st: b.st, ticker: b.h.ticker }));

  const html = ({ h, st }) => stanceCard(st, {
    ticker: h.ticker,
    sub: [h.type, h.broker].filter(Boolean).join(' \u00b7 '),
    // ⚠ ASELS IS A HOLDING AND AN OPEN TRADE AT THE SAME TIME, so the identical unresolved SELL
    // sits against both. Both cards are shown — they are different positions with different sizes
    // and exits, and silently omitting a live conflict is the worse failure — but this one says
    // where the reasoning already is instead of repeating it.
    chip: st.alsoOpenTrade ? '<span class="stnchip xref">\u25c6 also an open trade</span>' : '',
    sideNote: 'A holding has no side of its own; it is read as long, because a position you own '
      + 'can only be hurt by a bearish call.',
  });

  const open = cards.filter(c => c.st.verdict === 'conflicted');
  const rest = cards.filter(c => c.st.verdict !== 'conflicted');
  const shown = open.length ? open : cards.slice(0, 1);
  const hidden = open.length ? rest : cards.slice(1);

  if (nEl) nEl.textContent = String(shown.length);
  host.innerHTML = shown.map(html).join('');

  if (moreD) {
    if (hidden.length) {
      moreD.style.display = '';
      const by = {};
      hidden.forEach(c => { by[c.st.verdict] = (by[c.st.verdict] || 0) + 1; });
      const LBL = { superseded: 'stood down', agrees: 'agrees', nocall: 'no call', unreadable: 'unreadable' };
      if (moreN) moreN.textContent = '(' + Object.keys(by).map(k => by[k] + ' ' + (LBL[k] || k)).join(' \u00b7 ') + ')';
      if (more) more.innerHTML = hidden.map(html).join('');
    } else {
      moreD.style.display = 'none';
      if (more) more.innerHTML = '';
    }
  }

  const meta = $('pnStanceMeta');
  if (meta) {
    meta.innerHTML = (open.length ? `<b style="color:#f5a524">${open.length} conflicted</b> \u00b7 ` : '')
      + `${cards.length} of ${seen.size} holding${seen.size === 1 ? '' : 's'} have a tape`
      + ' \u00b7 reading, not advice';
  }
}

export function renderP(){
  renderPortfolioBar();
  renderPortfolioList();
  const sr=document.getElementById('hSrch').value.toLowerCase(),tf=document.getElementById('hTpF').value,bf=document.getElementById('hBkF').value;
  const apf = S.activePortfolioId; // active portfolio filter
  let items=S.holdings.filter(h=>{if(apf&&h.portfolioId!==apf)return false;if(tf!=='all'&&h.type!==tf)return false;if(bf!=='all'&&h.broker!==bf)return false;
  if(sr&&!h.ticker.toLowerCase().includes(sr)&&!h.name.toLowerCase().includes(sr))return false;return true;});
  if(sortC)items.sort((a,b)=>{let va=a[sortC]??0,vb=b[sortC]??0;
  if(sortC==='_pnl'){va=hPnl(a).pnl||0;vb=hPnl(b).pnl||0;}return(va<vb?-1:va>vb?1:0)*sortD;});

  // Scope summary to filtered portfolio (or all)
  const scopedH = apf ? S.holdings.filter(h => h.portfolioId === apf) : S.holdings;
  // Unpriced holdings are counted at COST in both invested and value (matches the neo
  // view + dashboard twUSD), so Current Value / P&L / weights are consistent everywhere.
  // A closed position contributes to NEITHER value nor invested. Leaving it in `gInv` while
  // hWealthUSD keeps it out of `gVal` would turn every exit into a fabricated loss.
  let gInv=0,gVal=0,gNoPrice=0;scopedH.forEach(h=>{if(!(h.qty>0))return;const w=hWealthUSD(h);gInv+=hTotUSD(h);gVal+=w.usd;if(w.atCost)gNoPrice++;});
  const gP=gVal-gInv,gPP=gInv>0?(gP/gInv)*100:0,hv=gVal>0;
  // ⚠ A FOURTH NET WORTH USED TO BE COMPUTED HERE — a trade-P&L loop, `cashTotalUSD()` and
  // `const _tw = gVal + totalCashUSD + tPnL`, the whole block read by NOTHING. Dead since the
  // Portfolio hero moved onto the canonical provider; found on 2026-08-16 by the new
  // quantity-registry row `trade.marketValue`, whose second shape hunts exactly this formula.
  // Deleted rather than exempted (rule #22): net worth comes from netWorthUSD(), trap #13, and a
  // dead copy is still a copy waiting to be read by the next person who needs a total here.



  const manN=S.holdings.filter(h=>PS[h.ticker]?.s==='m'&&!S.prices[h.ticker]).length;
  document.getElementById('pSum').innerHTML=`
  <div class="neo-cell"><div class="l">Invested</div><div class="v">${fU(gInv)}</div><div class="s">${S.fxRate?fT(gInv*S.fxRate):'<span class="neu">FX…</span>'}</div></div>
  <div class="neo-cell"><div class="l">Current Value</div><div class="v">${hv?fU(gVal):'<span class="neu">—</span>'}${gNoPrice?` <span style="font-size:10px;color:var(--t3)">⏳ ${gNoPrice} at cost</span>`:''}</div><div class="s">${hv?(S.fxRate?fT(gVal*S.fxRate):'<span class="neu">FX…</span>'):''}</div></div>
  <div class="neo-cell"><div class="l">Unrealized P&L</div><div class="v ${pc(gP)}">${hv?fU(gP):'—'}</div><div class="s ${pc(gPP)}">${hv?fP(gPP):''}</div></div>
  <div class="neo-cell"><div class="l">Monthly</div><div class="v">${fU(S.base)}</div><div class="s">${getMs().length} months</div></div>
  <div class="neo-cell"><div class="l">Holdings</div><div class="v">${S.holdings.length}</div><div class="s">${f(S.holdings.reduce((s,h)=>s+h.allocationPct,0),1)}% alloc</div></div>
  <div class="neo-cell"><div class="l">Prices</div><div class="v">${Object.keys(S.prices).length}/${S.holdings.length}</div><div class="s">${manN?manN+' manual needed':'All fetched'}</div></div>
  ${(()=>{const xr=hv?calcPortfolioXIRR():null;const xp=xr!==null?xr*100:null;return xp!==null?`<div class="neo-cell"><div class="l">Portfolio XIRR</div><div class="v ${pc(xp)}">${fP(xp)}</div><div class="s">Annualized return</div></div>`:'';})()}
  ${(()=>{const td=(S.dividends||[]).reduce((s,d)=>s+dividendUSD(d),0);return td>0?`<div class="neo-cell"><div class="l">Total Dividends</div><div class="v pos">${fU(td)}</div><div class="s">${(S.dividends||[]).length} payment(s)</div></div>`:'';})()}`;

  const ta={};S.holdings.forEach(h=>{ta[h.type]=(ta[h.type]||0)+h.allocationPct;});
  const tc={Stock:'#7ff0c4',ETF:'#8b5cf6',Fund:'#a78bfa',Commodity:'#FF8C42',Cash:'#4DC9F6',Crypto:'#D4A853'};
  document.getElementById('allocBar').innerHTML=Object.entries(ta).map(([t,p])=>`<div style="width:${p}%;background:${tc[t]||'#8b5cf6'}">${t} ${f(p,1)}%</div>`).join('');
  document.getElementById('allocLbls').textContent=Object.entries(ta).map(([t,p])=>`${t}: ${f(p,1)}%`).join(' · ');

  // === Rebalancing Check ===
  const td={};S.holdings.forEach(h=>{if(!td[h.type])td[h.type]={inv:0,val:0,n:0};td[h.type].inv+=hTotUSD(h);const v=hVal(h);if(v.vU)td[h.type].val+=v.vU;td[h.type].n++;});
  const totalVal=Object.values(td).reduce((s,d)=>s+d.val,0);
  const rebalEl=document.getElementById('rebalWarn');
  if(rebalEl&&totalVal>0){
    const rebalThreshold=parseFloat(S.settings?.rebalThreshold)||5;
    const rebalData=Object.entries(ta).map(([type,targetPct])=>{
      const actualVal=td[type]?td[type].val:0;
      const actualPct=totalVal>0?(actualVal/totalVal)*100:0;
      const dev=actualPct-targetPct;
      return{type,targetPct,actualPct,dev,color:tc[type]||'#8b5cf6'};
    });
    const alerts=rebalData.filter(r=>Math.abs(r.dev)>rebalThreshold);
    rebalEl.style.display='block';
    document.getElementById('rebalStatus').textContent=alerts.length?`⚠ ${alerts.length} type(s) off-target (>${rebalThreshold}%)`:'✓ All within threshold';
    document.getElementById('rebalStatus').style.color=alerts.length?'var(--yel)':'var(--grn)';
    document.getElementById('rebalGrid').innerHTML=rebalData.map(r=>{
      const absD=Math.abs(r.dev);const isOff=absD>rebalThreshold;
      const devColor=r.dev>rebalThreshold?'var(--grn)':r.dev<-rebalThreshold?'var(--red)':'var(--t3)';
      const arrow=r.dev>0.5?'▲':r.dev<-0.5?'▼':'';
      return`<div class="gls-soft" style="border:1px solid var(--bd);border-radius:6px;padding:8px 10px;border-left:3px solid ${isOff?devColor:r.color}">
        <div style="font-size:11px;font-weight:600;color:${r.color}">${esc(r.type)}</div>
        <div style="display:flex;justify-content:space-between;margin-top:4px">
          <div style="font-size:10px;color:var(--t3)">Target<br><span style="color:var(--t1);font-size:12px;font-weight:600">${f(r.targetPct,1)}%</span></div>
          <div style="font-size:10px;color:var(--t3)">Actual<br><span style="color:var(--t1);font-size:12px;font-weight:600">${f(r.actualPct,1)}%</span></div>
        </div>
        <div style="font-size:11px;font-weight:600;color:${devColor};margin-top:4px;text-align:center">${arrow} ${r.dev>0?'+':''}${f(r.dev,1)}%</div>
        ${isOff?`<div style="margin-top:4px;height:3px;border-radius:2px;background:rgba(150,165,200,.12);overflow:hidden"><div style="width:${Math.min(absD/rebalThreshold*50,100)}%;height:100%;background:${devColor};border-radius:2px"></div></div>`:''}</div>`;
    }).join('');
  }else if(rebalEl){rebalEl.style.display='none';}
  document.getElementById('typeGrid').innerHTML=Object.entries(td).map(([t,d])=>{const p=d.val-d.inv,pp=d.inv>0?(p/d.inv)*100:0;
  return`<div class="type-card"><div class="tc-type" style="color:${tc[t]}">${esc(t)} (${d.n})</div>
  <div style="font-size:11px;color:var(--t2)">Inv: ${fU(d.inv)}</div><div style="font-size:16px;font-weight:700">${d.val>0?fU(d.val):'—'}</div>
  <div style="font-size:11px" class="${pc(p)}">${d.val>0?fU(p)+' ('+fP(pp)+')':'—'}</div></div>`;}).join('');

  renderAllocCh(td);

  document.getElementById('hBody').innerHTML=items.map(h=>{
    const iU=hTotUSD(h),iL=hTotLocal(h),v=hVal(h),pnl=hPnl(h),p=S.prices[h.ticker],ps=PS[h.ticker];
    const wt=gVal>0?((v.vU||iU)/gVal)*100:0;const ls=h.currency==='TRY'?'₺':h.currency==='EUR'?'€':'$';
    const isM=ps&&ps.s==='m';const stale=p&&(Date.now()-p.ts>86400000);
    const srcD=!p?(isM?'dot-manual':'dot-stale'):stale?'dot-stale':'dot-live';
    const srcL=!p?(isM?'Manual':'Pending'):(p.src==='manual'?'Manual':'Auto');
    const lotRow = _holdingDetailRow(h, ls, { colspan: 16, trAttrs: 'class="lot-detail"',
      tdStyle: 'padding:4px 12px', btn: 'btn btn-s', btnStyle: 'font-size:9px;padding:1px 6px',
      acc: 'var(--acc)', grn: 'var(--grn)' });
    return`<tr><td><strong>${esc(h.ticker)}</strong><br><span style="color:var(--t3);font-size:10px">${esc(h.portfolioName||h.name)}</span></td>
    <td><span class="badge ${bc(h.type)}">${esc(h.type)}</span></td><td style="color:var(--t3);font-size:11px">${esc(h.subType)}</td><td>${esc(h.broker)}</td>
    <td class="tr" style="font-size:11px">${h.qty?f(h.qty,h.type==='Cash'?2:4):'0'}</td><td class="tr"><strong>${f(h.allocationPct,1)}%</strong></td><td class="tr">${ls}${f(iL)}</td><td class="tr">${fU(iU)}</td>
    <td class="tr">${p?`<span class="${pc(p.changePct)}" style="cursor:pointer" title="Click to edit" data-action="inlinePrice" data-args='["${h.ticker}","${h.currency}",${p.price}]'>${fByCur(h.currency,p.price)}</span>`:`<input class="price-input" placeholder="Enter..." data-action-change="manP" data-args='["${h.ticker}","${h.currency}"]'>`}</td>
    <td class="tr"><span class="price-src"><span class="dot ${srcD}"></span>${srcL}</span></td>
    <td class="tr">${v.vU?fU(v.vU):'—'}</td><td class="tr ${pc(pnl.pnl)}">${pnl.pnl!=null?fU(pnl.pnl):'—'}</td>
    <td class="tr ${pc(pnl.pnlP)}">${pnl.pnlP!=null?fP(pnl.pnlP):'—'}</td>
    <td class="tr">${(()=>{const xr=calcHoldingXIRR(h);return xr!==null?`<span class="${pc(xr*100)}">${fP(xr*100)}</span>`:'—';})()}</td>
    <td class="tr">${(()=>{const dv=holdingDividends(h.ticker);return dv.total>0?`<span class="pos" style="cursor:pointer" title="${dv.count} dividend(s)" data-action="openDivModal" data-args='["${h.ticker}","${h.currency}"]'>${fByCur(h.currency,dv.total)}</span>`:`<button class="btn btn-s" style="font-size:9px;padding:1px 6px" data-action="openDivModal" data-args='["${h.ticker}","${h.currency}"]'>+</button>`;})()}</td>
    <td><div class="wbar"><div class="wbar-f" style="width:${Math.min(wt*3,100)}%"></div></div><span style="font-size:10px">${f(wt,1)}%</span></td>
    <td><button class="btn btn-s" data-action="editH" data-args='["${h.id}"]'>Edit</button> <button class="btn btn-s btn-d" data-action="delH" data-args='["${h.id}"]'>×</button></td></tr>${lotRow}`;}).join('')||'<tr><td colspan="16" class="neo-empty">No holdings match your filters</td></tr>';
  try { _portNeo(); } catch (e) { console.error('[ZAGX] _portNeo failed:', e); }
}

/**
 * _portNeo() — feeds the neon Portfolio (#portNeo) from real data.
 * Legacy portfolio (#portLegacy) stays hidden so renderP's connections keep working.
 * KPIs + holdings table update every render; donut/broker/allocation-explorer build once (anim).
 */
function _portNeo() {
  const SPK = window.SPK; const $ = id => document.getElementById(id);
  if (!$('pnBody')) return;
  const apf = S.activePortfolioId; // active portfolio filter (null = All)
  const scopedHoldings = apf ? S.holdings.filter(h => h.portfolioId === apf) : S.holdings;
  const rows = scopedHoldings.map(h => {
    const v = hVal(h); const inv = hTotUSD(h); const val = v.vU || 0; const p = S.prices[h.ticker];
    const priced = val > 0;                 // unpriced holdings have no live value
    const effVal = priced ? val : inv;      // count unpriced at COST (consistent with legacy summary + dashboard twUSD)
    const pnlP = (priced && inv > 0) ? ((val - inv) / inv * 100) : null;
    return { h, t: h.ticker, ty: h.type, br: h.broker, qty: h.qty, price: p ? p.price : null, val, inv, effVal, priced, pnlP };
  });
  const totVal = rows.reduce((s, r) => s + r.effVal, 0) || 1;
  const totInv = rows.reduce((s, r) => s + r.inv, 0);
  const curVal = rows.reduce((s, r) => s + r.effVal, 0);
  const nUnpriced = rows.filter(r => !r.priced).length;
  const upnl = curVal - totInv, upnlP = totInv > 0 ? upnl / totInv * 100 : 0;
  const valid = rows.filter(r => r.priced);
  const best = valid.slice().sort((a, b) => b.pnlP - a.pnlP)[0], worst = valid.slice().sort((a, b) => a.pnlP - b.pnlP)[0];
  // ===== Hero (Heroes Mockup v0.1) — headline Current Value + chips + context + 90d spark =====
  const hn = $('pfHeroNum');
  if (hn) {
    hn.innerHTML = fU(curVal) + (nUnpriced ? ` <span style="font-size:11px;color:var(--t3)">⏳ ${nUnpriced} at cost</span>` : '');
    const xr = calcPortfolioXIRR();
    // Employer-match reclass (2026-07-21): match ≠ dividend — separate chips, separate truths
    let divUSD = 0, matchUSD = 0;
    (S.dividends || []).forEach(d => { const v = dividendUSD(d); if (d.kind === 'match') matchUSD += v; else divUSD += v; });
    $('pfHeroChips').innerHTML =
      `<span class="neo-chip">Invested <b>${fU(totInv)}</b></span>` +
      `<span class="neo-chip">Unreal P&amp;L <b class="${upnl >= 0 ? 'pos' : 'neg'}">${upnl >= 0 ? '+' : ''}${fU(upnl)} · ${fP(upnlP)}</b></span>` +
      (xr !== null ? `<span class="neo-chip" title="Money-weighted, annualized. Includes employer-match shares received at no cost.">XIRR <b class="hx">${fP(xr * 100)}</b></span>` : '') +
      (divUSD > 0 ? `<span class="neo-chip">Dividends <b>${fU(divUSD)}</b></span>` : '') +
      (matchUSD > 0 ? `<span class="neo-chip" title="Employer plan contribution — earnings, but not passive income">Employer match <b class="pos">${fU(matchUSD)}</b></span>` : '');
    const tryVal = rows.filter(r => r.h.currency === 'TRY').reduce((s, r) => s + r.effVal, 0);
    const top3 = rows.slice().sort((a, b) => b.effVal - a.effVal).slice(0, 3).reduce((s, r) => s + r.effVal, 0);
    $('pfHeroCtx').innerHTML = (best && worst)
      ? `Best <b class="pos">${esc(best.t)} ${fP(best.pnlP)}</b> · worst <b class="neg">${esc(worst.t)} ${fP(worst.pnlP)}</b> · TRY exposure <b>${Math.round(tryVal / totVal * 100)}%</b> · top-3 concentration ${Math.round(top3 / totVal * 100)}% of book`
      : 'Prices pending — context builds once holdings are priced.';
    // 90d value sparkline from real snapshots (never fabricated; empty when <2 points)
    const svg = $('pfHeroSpark');
    if (svg && SPK) {
      const pts = (S.snapshots || []).slice(-90).map(s => s.portfolioValue).filter(v => v > 0);
      const sig = pts.length + ':' + (pts[pts.length - 1] || 0).toFixed(0);
      if (svg._pfSig !== sig) { svg._pfSig = sig; SPK.spark(svg, '#9B4DFF', pts, { animate: !svg._pfDrawn }); svg._pfDrawn = true; }
    }
  }
  // ===== KPI row (ids kept, labels remapped — hero absorbed Invested/Current Value/Unreal P&L) =====
  $('pnInv').textContent = fU(S.base);
  const moN = getMs().length;
  // LIVE call via main.js wiring (not a stale window global) — dashboard only refreshes
  // __twUSD-style globals when IT renders; cold boot left them at default-state values.
  const ng = (typeof window !== 'undefined' && window.__netGrowth) ? window.__netGrowth() : null;
  const nv = $('pnVal');
  if (ng) { nv.className = 'v ' + pc(ng.twPnL); nv.textContent = (ng.twPnL >= 0 ? '+' : '') + fU(ng.twPnL) + ' (' + fP(ng.twPP) + ')'; }
  else { nv.className = 'v'; nv.textContent = '—'; }
  $('pnPnl').textContent = rows.length;
  $('pnBW').innerHTML = (best && worst) ? `<span class="pos">${esc(best.t)} ${fP(best.pnlP)}</span> · <span class="neg">${esc(worst.t)} ${fP(worst.pnlP)}</span>` : '—';
  // header meta + KPI story sub-lines (same numbers, presentation only)
  const pm = $('portMeta');
  if (pm) pm.innerHTML = `<b>${rows.length}</b> holdings · ${fU(curVal)} · <b class="${upnl >= 0 ? 'pos' : 'neg'}">${fP(upnlP)}</b> unrealized`; // fP already signs (was rendering "++")
  const sub = (id, txt) => { const e = $(id); if (e) e.textContent = txt; };
  sub('pnInvS', moN ? `${moN} months in` : 'no months yet');
  sub('pnValS', ng ? 'with deposits · live' : 'needs external-capital history');
  sub('pnPnlS', (() => { const set = scopedHoldings.filter(h => h.allocationPct > 0).length; return `alloc targets ${rows.length ? Math.round(set / rows.length * 100) : 0}% set`; })());
  // holdings table (every render) — search/type/broker filters apply to TABLE ONLY (KPIs/widgets stay portfolio-scoped, like legacy)
  const sr = ($('pnSrch')?.value || '').toLowerCase();
  const tf = $('pnTpF')?.value || 'all';
  const bf = $('pnBkF')?.value || 'all';
  const visRows = rows.filter(r => {
    const h = r.h;
    if (tf !== 'all' && h.type !== tf) return false;
    if (bf !== 'all' && h.broker !== bf) return false;
    if (sr && !((h.ticker || '').toLowerCase().includes(sr) || (h.name || '').toLowerCase().includes(sr))) return false;
    return true;
  });
  _renderHoldingStance(rows);
  $('pnCnt').textContent = visRows.length;
  $('pnBody').innerHTML = visRows.sort((a, b) => b.effVal - a.effVal).map(r => {
    const h = r.h;
    // Mirror legacy holdings row: native price (inline-edit) + cost local/USD + value USD + P&L + XIRR + dividends.
    // Unpriced holdings: value shown at COST (⏳), consistent with KPI Current Value.
    const iU = hTotUSD(h), iL = hTotLocal(h), v = hVal(h), pnl = hPnl(h), p = S.prices[h.ticker], ps = PS[h.ticker];
    const w = r.effVal / totVal * 100;
    const isCash = r.ty === 'Cash';
    const ls = h.currency === 'TRY' ? '₺' : h.currency === 'EUR' ? '€' : '$';
    const isM = ps && ps.s === 'm'; const stale = p && (Date.now() - p.ts > 86400000);
    const srcD = !p ? (isM ? 'dot-manual' : 'dot-stale') : stale ? 'dot-stale' : 'dot-live';
    const srcL = !p ? (isM ? 'Manual' : 'Pending') : (p.src === 'manual' ? 'Manual' : 'Auto');
    const priceCell = p
      ? `<span class="${pc(p.changePct)}" style="cursor:pointer" title="Click to edit" data-action="inlinePrice" data-args='["${h.ticker}","${h.currency}",${p.price}]'>${fByCur(h.currency, p.price)}</span>`
      : `<input class="price-input" placeholder="Enter…" data-action-change="manP" data-args='["${h.ticker}","${h.currency}"]'>`;
    const valCell = r.priced ? `<b>${fU(r.val)}</b>` : `<span title="awaiting price — shown at cost" style="color:var(--t2)">⏳ ${fU(r.inv)}</span>`;
    const xr = calcHoldingXIRR(h);
    const dv = holdingDividends(h.ticker);
    const divCell = dv.total > 0
      ? `<span class="pos" style="cursor:pointer" title="${dv.count} dividend(s)" data-action="openDivModal" data-args='["${h.ticker}","${h.currency}"]'>${fByCur(h.currency, dv.total)}</span>`
      : `<button class="glassbtn" style="padding:1px 7px" data-action="openDivModal" data-args='["${h.ticker}","${h.currency}"]'>+</button>`;
    const lotRow = _holdingDetailRow(h, ls, { colspan: 17, trAttrs: 'style="background:rgba(255,255,255,.025)"',
      tdStyle: 'padding:5px 14px', btn: 'glassbtn', btnStyle: 'padding:1px 8px',
      acc: 'var(--cyan)', grn: '#34e2a0' });
    return `<tr>
      <td class="txt"><span style="display:flex;align-items:center;gap:8px"><span class="tk">${(r.t || '').slice(0, 4)}</span><b>${esc(r.t)}</b></span><div style="color:var(--t3);font-size:10px;padding-left:30px">${esc(h.portfolioName || h.name || '')}</div></td>
      <td class="txt"><span class="badge ${bc(h.type)}">${esc(h.type)}</span></td>
      <td style="color:var(--t3);font-size:11px">${esc(h.subType || '')}</td>
      <td style="color:var(--t3)">${esc(h.broker || '')}</td>
      <td>${h.qty ? f(h.qty, isCash ? 2 : 4) : '0'}</td>
      <td><b>${f(h.allocationPct, 1)}%</b></td>
      <td style="color:var(--t2)">${ls}${f(iL)}</td>
      <td style="color:var(--t2)">${fU(iU)}</td>
      <td>${priceCell}</td>
      <td><span class="price-src"><span class="dot ${srcD}"></span>${srcL}</span></td>
      <td>${valCell}</td>
      <td class="${pc(pnl.pnl)}">${pnl.pnl != null ? fU(pnl.pnl) : '—'}</td>
      <td class="${pc(pnl.pnlP)}">${pnl.pnlP != null ? fP(pnl.pnlP) : '—'}</td>
      <td>${xr !== null ? `<span class="${pc(xr * 100)}">${fP(xr * 100)}</span>` : '—'}</td>
      <td>${divCell}</td>
      <td><span class="wbar"><i style="width:${Math.min(w * 3, 100)}%"></i></span> <span style="color:var(--t3);font-size:11px">${w.toFixed(1)}%</span></td>
      <td style="white-space:nowrap"><button class="glassbtn" data-action="editH" data-args='["${h.id}"]' style="padding:3px 9px">Edit</button> <button class="glassbtn" data-action="delH" data-args='["${h.id}"]' style="padding:3px 8px;color:#ff5470">×</button></td>
    </tr>${lotRow}`;
  }).join('') || '<tr><td colspan="17" class="neo-empty">No holdings</td></tr>';

  // shared class mapping (used by High widgets every render + build-once visuals)
  const COL = { 'US ETF': '#00f0ff', 'US Stock': '#6aa0ff', 'TR Stock': '#ff2bd6', 'TR Fund': '#34e2a0', Commodity: '#e8b84b', Crypto: '#a78bfa', Cash: '#4678ff', Other: '#9aa6c7' };
  const cls = h => { const t = h.type, tr = (h.currency === 'TRY') || /garanti|bist|matriks|midas/i.test(h.broker || ''); if (t === 'Crypto') return 'Crypto'; if (t === 'Commodity') return 'Commodity'; if (t === 'Fund') return 'TR Fund'; if (t === 'ETF') return 'US ETF'; if (t === 'Stock') return tr ? 'TR Stock' : 'US Stock'; return 'Other'; };
  const classGroups = () => { const g = {}; rows.forEach(r => { if (r.effVal <= 0) return; const c = cls(r.h); g[c] = (g[c] || 0) + r.effVal; }); return g; };

  // ===== Rebalancing drift — actual (value %) vs target (allocationPct), by class (every render) =====
  // Scope fix 2026-07-24: actual and target must come from the SAME holding set. On the All tab
  // that set is the Retirement sleeve (the only one with targets); on a portfolio tab, that tab.
  const rebEl = $('pnRebal');
  if (rebEl) {
    const driftH = apf ? scopedHoldings : driftHoldings();
    if (!sleeveHasTargets(driftH)) {
      // A sleeve with no targets has no drift to show; every class would read +100% against
      // a zero target. Say so instead of drawing a false deviation.
      rebEl.innerHTML = '<div class="neo-empty">No allocation targets in this sleeve</div>';
    } else {
    const driftIds = new Set(driftH.map(h => h.id));
    const actG = {}; rows.forEach(r => { if (r.effVal <= 0 || !driftIds.has(r.h.id)) return; const c = cls(r.h); actG[c] = (actG[c] || 0) + r.effVal; });
    const actTot = Object.values(actG).reduce((a, b) => a + b, 0) || 1;
    const tgtG = {}; driftH.forEach(h => { const c = cls(h); tgtG[c] = (tgtG[c] || 0) + (h.allocationPct || 0); });
    const tgtTot = Object.values(tgtG).reduce((a, b) => a + b, 0) || 1;
    const classes = [...new Set([...Object.keys(actG), ...Object.keys(tgtG)])];
    const data = classes.map(c => ({ c, act: (actG[c] || 0) / actTot * 100, tgt: (tgtG[c] || 0) / tgtTot * 100 })).sort((a, b) => b.act - a.act);
    const mx = Math.max(...data.map(d => Math.max(d.act, d.tgt)), 1) * 1.1;
    rebEl.innerHTML = data.map(d => { const dev = d.act - d.tgt; const dc = dev > 1 ? 'var(--grn)' : dev < -1 ? 'var(--red)' : 'var(--t3)';
      return `<div class="rbrow"><div class="rblbl">${esc(d.c)}</div><div class="rbtrack"><div class="rbact" style="width:${(d.act / mx * 100).toFixed(1)}%;background:${COL[d.c] || COL.Other}"></div><div class="rbtgt" style="left:${(d.tgt / mx * 100).toFixed(1)}%"></div></div><div class="rbdev" style="color:${dc}">${dev > 0 ? '+' : ''}${dev.toFixed(1)}%</div></div>`;
    }).join('') || '<div class="neo-empty">No allocation set</div>';
    }
    // review/DCA-lock status badge (read cached state — no heavy recompute in render)
    const stEl = $('pnRebalStatus');
    if (stEl) {
      const rb = S.rebalancing || {}; const ld = rb.lastDrift || {};
      if (rb.dcaLocked) { stEl.textContent = '● DCA locked'; stEl.style.color = 'var(--red)'; stEl.title = rb.dcaLockedReason || 'Rebalance review required'; }
      else if (ld.reviewDue) { stEl.textContent = '● review due'; stEl.style.color = 'var(--yel)'; stEl.title = 'Rebalance review window open'; }
      else { stEl.textContent = '● on track'; stEl.style.color = 'var(--grn)'; stEl.title = ld.lastReview ? ('Last review ' + String(ld.lastReview).slice(0, 10)) : ''; }
    }
  }
  // ===== Dividends & forward yield (from S.dividends, every render) =====
  const divEl = $('pnDiv');
  if (divEl) {
    const yrAgo = Date.now() - 365 * 86400000;
    const dvUSD = dividendUSD;
    const divs = (S.dividends || []).filter(d => isPassiveDividend(d) && d.date && new Date(d.date).getTime() >= yrAgo);
    const ttm = divs.reduce((s, d) => s + dvUSD(d), 0);
    const fwdYield = curVal > 0 ? (ttm / curVal * 100) : 0;
    const months = []; const dt = new Date(); dt.setDate(1);
    for (let i = 11; i >= 0; i--) { const m = new Date(dt.getFullYear(), dt.getMonth() - i, 1); months.push({ key: m.getFullYear() + '-' + (m.getMonth() + 1), lab: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][m.getMonth()], v: 0 }); }
    divs.forEach(d => { const m = new Date(d.date); const key = m.getFullYear() + '-' + (m.getMonth() + 1); const b = months.find(x => x.key === key); if (b) b.v += dvUSD(d); });
    const dmax = Math.max(...months.map(m => m.v), 1);
    divEl.innerHTML = `<div style="display:flex;gap:18px;align-items:baseline;margin-bottom:6px">
      <div><div style="font-family:var(--mono);font-size:24px;font-weight:700;color:var(--grn)">${fU(ttm)}</div><div style="font-size:10px;color:var(--t3)">trailing 12-mo net</div></div>
      <div><div style="font-family:var(--mono);font-size:16px;font-weight:600;color:var(--cyan)">${fwdYield.toFixed(1)}%</div><div style="font-size:10px;color:var(--t3)">forward yield</div></div>
      <div><div style="font-family:var(--mono);font-size:16px;font-weight:600">${fU(ttm / 12)}/mo</div><div style="font-size:10px;color:var(--t3)">run-rate</div></div></div>
      <div class="dvbars">${months.map(m => `<div class="dvb" style="height:${Math.max(m.v / dmax * 100, 2).toFixed(0)}%"><span>${m.lab}</span></div>`).join('')}</div>`;
  }
  // ===== Contribution to return waterfall (Medium widget) =====
  const wfEl = $('pnWf'), wfNet = $('pnWfNet');
  if (wfEl) {
    const all = rows.filter(r => r.priced).map(r => ({ tk: r.t, pnl: r.val - r.inv }));
    const net = all.reduce((s, c) => s + c.pnl, 0);
    const sig = all.filter(c => Math.abs(c.pnl) >= 50);
    const oth = all.filter(c => Math.abs(c.pnl) < 50).reduce((s, c) => s + c.pnl, 0);
    const arr = sig.slice(); if (Math.abs(oth) > 1) arr.push({ tk: 'Others', pnl: oth });
    arr.sort((a, b) => b.pnl - a.pnl);
    const maxAbs = Math.max(...arr.map(c => Math.abs(c.pnl)), 1);
    wfEl.innerHTML = arr.map(c => { const h = Math.abs(c.pnl) / maxAbs * 100; const col = c.pnl >= 0 ? 'var(--grn)' : 'var(--red)'; return `<div class="wfcol"><div class="wfval ${c.pnl >= 0 ? 'pos' : 'neg'}">${c.pnl >= 0 ? '+' : ''}${Math.round(c.pnl)}</div><div class="wfbar" style="height:${h.toFixed(0)}%;background:${col}"></div><div class="wflab">${esc(c.tk)}</div></div>`; }).join('') || '<div class="neo-empty">No priced holdings</div>';
    if (wfNet) wfNet.innerHTML = `<span>net unrealized</span><span class="${net >= 0 ? 'pos' : 'neg'}" style="font-family:var(--mono)">${net >= 0 ? '+' : ''}${fU(net)}</span>`;
  }
  // ===== Concentration risk =====
  const concEl = $('pnConc'), concHead = $('pnConcHead'), cmeter = $('pnCmeter');
  if (concEl) {
    const sorted = rows.filter(r => r.effVal > 0).slice().sort((a, b) => b.effVal - a.effVal);
    const tv = sorted.reduce((s, r) => s + r.effVal, 0) || 1;
    const top5 = sorted.slice(0, 5).reduce((s, r) => s + r.effVal, 0) / tv * 100;
    const hhi = sorted.reduce((s, r) => s + Math.pow(r.effVal / tv, 2), 0) * 10000;
    const lvl = hhi < 1500 ? 'diversified' : hhi < 2500 ? 'moderate' : 'concentrated';
    if (concHead) concHead.innerHTML = `<div style="font-family:var(--mono);font-size:24px;font-weight:700;color:var(--yel,#e8b84b)">${top5.toFixed(1)}%</div><div style="font-size:11px;color:var(--t3)">top-5 of value · HHI ${Math.round(hhi)} (${lvl})</div>`;
    concEl.innerHTML = sorted.slice(0, 5).map(r => { const p = r.effVal / tv * 100; const c = COL[cls(r.h)] || COL.Other; return `<div class="crow"><div class="clbl">${esc(r.t)}</div><div class="ctrack"><div class="cfill" style="width:${Math.min(p * 2.5, 100).toFixed(0)}%;background:${c}"></div></div><div class="cval">${p.toFixed(1)}%</div></div>`; }).join('');
    if (cmeter) cmeter.style.cssText = `width:${Math.min(hhi / 4000 * 100, 100).toFixed(0)}%;background:linear-gradient(90deg,#34e2a0,#e8b84b,#ff5470)`;
  }
  // ===== DCA opportunities (averaging-down on losers) =====
  const dcaEl = $('pnDca');
  if (dcaEl) {
    const losers = rows.filter(r => r.priced && r.ty !== 'Cash' && r.pnlP != null && r.pnlP < -10 && r.qty > 0).sort((a, b) => a.pnlP - b.pnlP).slice(0, 3);
    if (!losers.length) dcaEl.innerHTML = '<div class="neo-empty">No holdings down >10%</div>';
    else dcaEl.innerHTML = losers.map(r => { const avg = r.inv / r.qty, price = r.val / r.qty, add = 500, addQty = add / price, newQty = r.qty + addQty, newAvg = (r.inv + add) / newQty, drop = (avg - newAvg) / avg * 100;
      return `<div class="dca"><div class="dcah"><b>${esc(r.t)}</b><span class="l neg">${r.pnlP.toFixed(1)}% · avg ${fU(avg)} → now ${fU(price)}</span></div><div class="dcag"><div class="c"><div class="k">Add</div><div class="v">$${add}</div></div><div class="c"><div class="k">New avg</div><div class="v" style="color:var(--cyan)">${fU(newAvg)}</div></div><div class="c"><div class="k">Avg drop</div><div class="v pos">−${drop.toFixed(1)}%</div></div></div></div>`; }).join('');
  }

  // ---- build-once visuals (donut / brokers / allocation explorer) ----
  if (window._portBuilt) return;
  if (!valid.length) return; // wait until priced
  window._portBuilt = true;
  // donut by class
  try {
    const grp = classGroups(); const tot = Object.values(grp).reduce((a, b) => a + b, 0) || 1;
    const data = Object.entries(grp).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([t, v]) => [t, Math.round(v / tot * 100), COL[t] || COL.Other]);
    SPK.donut($('pnDonut'), data, fU(tot));
    $('pnLeg').innerHTML = data.map(([n, p, c]) => `<div><i style="background:${c};box-shadow:0 0 8px ${c}"></i>${n}<b>${p}%</b></div>`).join('');
  } catch (e) { /* donut */ }
  // brokers
  try {
    const bg = {}; rows.forEach(r => { if (r.effVal <= 0) return; const b = r.br || '—'; bg[b] = (bg[b] || 0) + r.effVal; });
    const tot = Object.values(bg).reduce((a, b) => a + b, 0) || 1; const bcol = { IBKR: '#00f0ff', Garanti: '#34e2a0', XETRA: '#a78bfa', Binance: '#e8b84b' };
    $('pnBrk').innerHTML = Object.entries(bg).sort((a, b) => b[1] - a[1]).map(([n, v]) => { const p = Math.round(v / tot * 100); return `<div style="margin-bottom:9px"><div style="display:flex;justify-content:space-between;font-size:12px;color:var(--t2);margin-bottom:3px"><span>${esc(n)}</span><b style="font-family:var(--mono);color:var(--t1)">${p}%</b></div><span class="wbar" style="display:block;width:100%"><i style="width:${p}%;background:${bcol[n] || '#9aa6c7'}"></i></span></div>`; }).join('');
  } catch (e) { /* brokers */ }
  // allocation explorer
  const HV = rows.filter(r => r.effVal > 0).map(r => ({ t: r.t, value: r.effVal, pnl: r.priced ? +r.pnlP.toFixed(1) : 0 }));
  try { SPK.heatmap($('pnHeat'), HV); } catch (e) { /* treemap */ }
  const TAU = Math.PI * 2; let bubB = false, sunB = false;
  function bubble() {
    const wrap = $('pnBub'); const W = wrap.clientWidth || 640, Hh = 260; const mx = Math.max(...HV.map(d => d.value)) || 1;
    const bs = HV.map(d => { const r = 16 + Math.sqrt(d.value / mx) * 40; const c = d.pnl >= 0 ? '#34e2a0' : '#ff5470'; return { t: d.t, r, c, x: r + Math.random() * (W - 2 * r), y: r + Math.random() * (Hh - 2 * r), vx: (Math.random() - .5) * .4, vy: (Math.random() - .5) * .4 }; });
    wrap.innerHTML = bs.map((b, i) => `<div class="bub" id="pnb${i}" style="width:${b.r * 2}px;height:${b.r * 2}px;background:radial-gradient(circle at 35% 30%,${b.c}cc,${b.c}44);opacity:0;transition:opacity .6s">${b.t}</div>`).join('');
    bs.forEach((b, i) => setTimeout(() => { const e = $('pnb' + i); if (e) e.style.opacity = 1; }, i * 45));
    (function loop() { if (window.SPK && window.SPK.motionOK && !window.SPK.motionOK()) return; bs.forEach((b, i) => { b.x += b.vx; b.y += b.vy; if (b.x < b.r || b.x > W - b.r) b.vx *= -1; if (b.y < b.r || b.y > Hh - b.r) b.vy *= -1; const e = $('pnb' + i); if (e) e.style.transform = `translate(${(b.x - b.r).toFixed(1)}px,${(b.y - b.r).toFixed(1)}px)`; }); requestAnimationFrame(loop); })();
  }
  function sunburst() {
    const cx = 120, cy = 110; const pol = (r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    const seg = (r0, r1, a0, a1, col) => { const [x0, y0] = pol(r1, a0), [x1, y1] = pol(r1, a1), [x2, y2] = pol(r0, a1), [x3, y3] = pol(r0, a0); const arcLg = (a1 - a0) > Math.PI ? 1 : 0; return `<path d="M${x0.toFixed(1)} ${y0.toFixed(1)} A${r1} ${r1} 0 ${arcLg} 1 ${x1.toFixed(1)} ${y1.toFixed(1)} L${x2.toFixed(1)} ${y2.toFixed(1)} A${r0} ${r0} 0 ${arcLg} 0 ${x3.toFixed(1)} ${y3.toFixed(1)} Z" fill="${col}" opacity="0" style="transition:opacity .5s"/>`; };
    const grp = classGroups(); const tot = Object.values(grp).reduce((a, b) => a + b, 0) || 1; const data = Object.entries(grp).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
    let a = -Math.PI / 2, h = ''; const outer = [];
    data.forEach(([n, v]) => { const p = v / tot; const a1 = a + p * TAU; const c = COL[n] || COL.Other; h += seg(40, 70, a, a1, c); const mid = (a + a1) / 2; outer.push(seg(72, 96, a, mid, c + '99')); outer.push(seg(72, 96, mid, a1, c + '55')); a = a1; });
    const sun = $('pnSun'); sun.innerHTML = h + outer.join(''); sun.querySelectorAll('path').forEach((p, i) => setTimeout(() => p.style.opacity = 1, 120 + i * 60));
    $('pnSunLeg').innerHTML = data.map(([n, v]) => `<div><i style="background:${COL[n] || COL.Other};box-shadow:0 0 8px ${COL[n] || COL.Other}"></i>${n}<b>${Math.round(v / tot * 100)}%</b></div>`).join('');
  }
  const seg = $('pnViz');
  if (seg) seg.addEventListener('click', e => { const s = e.target.closest('span'); if (!s) return; document.querySelectorAll('#pnViz span').forEach(x => x.classList.toggle('on', x === s)); ['treemap', 'bubble', 'sunburst'].forEach(v => $('pnvp-' + v).classList.toggle('on', v === s.dataset.v)); if (s.dataset.v === 'bubble' && !bubB) { bubB = true; bubble(); } if (s.dataset.v === 'sunburst' && !sunB) { sunB = true; sunburst(); } });
}

export function manP(tkOrEvent,val,cr){
  let tk,p;
  if(tkOrEvent&&tkOrEvent.target){
    // Called via data-action-change delegation: manP(event)
    const e=tkOrEvent;
    const args=e.target.getAttribute('data-args');
    const parsed=args?JSON.parse(args):[];
    tk=parsed[0];cr=parsed[1]||'TRY';
    p=parseFloat(e.target.value);
  }else{
    tk=tkOrEvent;p=parseFloat(val);
  }
  if(!p)return;S.prices[tk]={price:p,changePct:0,src:'manual',ts:Date.now(),cur:cr||'TRY'};lg('price',tk,'Manual: '+p);markDirty('dash','trades','cash');save();renderP();}

export function inlinePrice(tk,cr,cur){
  // Find the clickable span via data-action attribute
  const el=document.querySelector(`[data-action="inlinePrice"][data-args*='"${tk}"']`);
  if(!el)return;
  const inp=document.createElement('input');inp.className='price-input';inp.type='number';inp.step='any';inp.value=cur;inp.style.width='90px';
  el.replaceWith(inp);inp.focus();inp.select();
  const commit=()=>{const v=parseFloat(inp.value);if(v&&v!==cur){manP(tk,v,cr);}else{renderP();}};
  inp.addEventListener('keydown',e=>{if(e.key==='Enter'){e.preventDefault();commit();}if(e.key==='Escape'){renderP();}});
  inp.addEventListener('blur',commit);}

// Holding CRUD
export function openAddH(){editHId=null;document.getElementById('moHT').textContent='Add Holding';
['hTk','hNm','hPn','hBw','hAl','hQt','hAc'].forEach(i=>document.getElementById(i).value='');
document.getElementById('hTp').value='ETF';document.getElementById('hSt').value='Equity';document.getElementById('hBk').value='IBKR';document.getElementById('hCr').value='USD';
// Pre-select active portfolio or default
_populatePortfolioSelect('hPort', S.activePortfolioId || (getDefaultPortfolio()?.id || ''));
document.getElementById('moH').classList.add('active');}

export function editH(id){const h=S.holdings.find(x=>x.id===id);if(!h)return;editHId=id;document.getElementById('moHT').textContent='Edit '+h.ticker;
document.getElementById('hTk').value=h.ticker;document.getElementById('hNm').value=h.name;document.getElementById('hTp').value=h.type;
document.getElementById('hSt').value=h.subType;document.getElementById('hPn').value=h.portfolioName||'';document.getElementById('hBk').value=h.broker;
document.getElementById('hBw').value=h.buyWindow||'';document.getElementById('hAl').value=h.allocationPct;document.getElementById('hCr').value=h.currency;
document.getElementById('hQt').value=h.qty||'';document.getElementById('hAc').value=h.avgCost||'';
_populatePortfolioSelect('hPort', h.portfolioId || '');
document.getElementById('moH').classList.add('active');}

export function saveH(){const tk=document.getElementById('hTk').value.trim().toUpperCase();if(!tk)return toast('Ticker required','a-r');
const alVal=parseFloat(document.getElementById('hAl').value)||0;
if(alVal<0||alVal>100)return toast('Allocation must be 0-100%','a-r');
const qtVal=parseFloat(document.getElementById('hQt').value)||0;
if(qtVal<0)return toast('Quantity cannot be negative','a-r');
const acVal=parseFloat(document.getElementById('hAc').value)||0;
if(acVal<0)return toast('Average cost cannot be negative','a-r');
const curVal=document.getElementById('hCr').value;
if(!VALID_CURRENCIES.includes(curVal))return toast('Invalid currency','a-r');
const portId = document.getElementById('hPort')?.value || (getDefaultPortfolio()?.id || '');
const d={ticker:tk,name:document.getElementById('hNm').value.trim()||tk,type:document.getElementById('hTp').value,subType:document.getElementById('hSt').value,
portfolioName:document.getElementById('hPn').value.trim(),broker:document.getElementById('hBk').value,buyWindow:document.getElementById('hBw').value.trim(),
allocationPct:alVal,currency:curVal,qty:qtVal,avgCost:acVal,portfolioId:portId};
if(editHId){const i=S.holdings.findIndex(x=>x.id===editHId);if(i>=0){Object.assign(S.holdings[i],d);stampItem(S.holdings[i]);}lg('holding',tk,'Updated');}
else{const h={id:uid(),...d};stampItem(h);S.holdings.push(h);lg('holding',tk,'Added');}editHId=null;save();clMo('moH');renderP();toast(tk+' saved','a-b');}

// Daily TD: close anytime — current accrued net interest → realized, principal+accrued → cash
export async function closeDailyTd(id){
  const t=S.trades.find(x=>x.id===id);if(!t||t.type!=='TimeDeposit'||!t.td||t.td.kind!=='daily')return;
  const a=tdAccrued(t.td);if(!a)return toast('No accrual yet','a-r');
  if(!await uiConfirm(`Net interest ${f(a.accruedNet)} ${t.currency} → realized; ${f(a.currentValue)} ${t.currency} → Cash.`,{title:`Close daily deposit ${t.ticker}?`,okText:'Close deposit'}))return;
  const cur=t.currency||'TRY';
  S.cash[cur]=(S.cash[cur]||0)+a.currentValue;
  S.cashTxs.unshift({id:uid(),date:now(),type:'td_close',amount:a.currentValue,currency:cur,note:`Close daily TD ${t.ticker} (principal ${f(t.td.principal)} + net ${f(a.accruedNet)})`});
  const pnlUSD=toUSD(a.accruedNet,cur);
  // `tradeId` was missing here and in `confirmMaturity` while `trades.js` has stamped it since
  // it was introduced -- two of the seven realized writers, both on the deposit path, and `t` is
  // right there in scope. See `utils.tradeForRealized`: two open TRYVD2 deposits plus one closed
  // one is all it takes for a ticker fallback to hand each deposit the other's interest.
  S.realized.push({id:uid(),tradeId:t.id,date:now(),ticker:t.ticker,qty:1,entry:t.td.principal,exit:a.currentValue,pnl:a.accruedNet,pnlUSD,currency:cur,reason:'Daily TD closed',kind:'interest'});
  if (!t.exits) t.exits = []; // DI-05: guard for pre-exits schema trades
  t.exits.push({date:now(),price:a.currentValue,qty:1,reason:'Closed',pnl:a.accruedNet,currency:cur});
  t.status='Closed';t.closeDate=now();t.td.confirmed=true;t.td.confirmedAt=now();
  stampItem(t); // CRITICAL: bump _updatedAt so Supabase merge keeps local mutation
  lg('trade',t.ticker,`Daily TD closed — net ${f(a.accruedNet)} ${cur}`);
  save();renderAll();toast(t.ticker+' closed','a-g');
}

/**
 * Change rate on a daily TimeDeposit.
 * Locks in accrued interest, creates new segment at new rate.
 * Called from UI: data-action="changeTdRate" data-args='["tradeId"]'
 */
export async function changeTdRate(id) {
  const t = S.trades.find(x => x.id === id);
  if (!t || t.type !== 'TimeDeposit' || !t.td || t.td.kind !== 'daily') return;
  const a = tdAccrued(t.td);
  if (!a) return toast('No accrual data', 'a-r');

  const cur = t.currency || 'TRY';
  const fCur = cur === 'TRY' ? fT : cur === 'EUR' ? v => '€' + f(v) : fU;
  const oldRate = a.rate;
  const currentValue = a.currentValue; // principal + net interest so far

  const newRate = await uiPrompt(
    `Current rate: %${oldRate}\n` +
    `Days open: ${a.daysOpen}\n` +
    `Accrued interest (net): ${fCur(a.accruedNet)}\n` +
    `Current value: ${fCur(currentValue)}\n\n` +
    `Enter new annual rate (%):`,
    oldRate.toString(),
    { title: `${t.ticker} — Change annual rate`, okText: 'Apply rate', inputType: 'number' }
  );

  if (newRate === null) return; // cancelled
  const parsed = parseFloat(newRate);
  if (isNaN(parsed) || parsed <= 0 || parsed > 200) return toast('Invalid rate', 'a-r');
  if (parsed === oldRate) return toast('Same rate — no change', 'a-w');

  const today = now().split('T')[0]; // YYYY-MM-DD

  // Initialize rateSegments if not present (migration from legacy single-rate)
  if (!t.td.rateSegments || t.td.rateSegments.length === 0) {
    t.td.rateSegments = [
      { startDate: t.td.startDate, rate: oldRate, startBalance: t.td.principal }
    ];
  }

  // Lock in accrued interest: new segment starts with current gross value as balance
  // Use gross (before withholding) as the compounding base
  const grossValue = a.gross; // principal + gross interest
  t.td.rateSegments.push({
    startDate: today,
    rate: parsed,
    startBalance: grossValue
  });

  // Update the annualRate to current active rate (backward compat)
  t.td.annualRate = parsed;

  // Log the rate change
  if (!t.td.rateHistory) t.td.rateHistory = [];
  t.td.rateHistory.push({
    date: today,
    fromRate: oldRate,
    toRate: parsed,
    accruedGross: a.grossInt,
    accruedNet: a.accruedNet,
    balanceAtChange: grossValue
  });

  stampItem(t);
  save();
  renderAll();
  toast(`${t.ticker}: ${oldRate}% → ${parsed}% (${fCur(a.accruedNet)} locked in)`, 'a-b');
}

// Trade-side maturity confirmation (monthly TDs)
export async function confirmMaturity(id){
  const t=S.trades.find(x=>x.id===id);if(!t||t.type!=='TimeDeposit'||!t.td)return;
  const a=tdAccrued(t.td);if(!a||!a.matured)return toast('Not matured yet','a-r');
  if(!await uiConfirm(`Net interest ${f(a.netInt)} ${t.currency} added to realized P&L; principal+interest ${f(a.netMaturity)} ${t.currency} → Cash.`,{title:`Confirm maturity for ${t.ticker}?`,okText:'Confirm maturity'}))return;
  const cur=t.currency||'TRY';
  S.cash[cur]=(S.cash[cur]||0)+a.netMaturity;
  S.cashTxs.unshift({id:uid(),date:now(),type:'td_mature',amount:a.netMaturity,currency:cur,note:`Maturity ${t.ticker} (principal ${f(t.td.principal)} + net interest ${f(a.netInt)})`});
  // Realized P&L = net interest (after withholding)
  const pnlUSD=toUSD(a.netInt,cur);
  S.realized.push({id:uid(),tradeId:t.id,date:now(),ticker:t.ticker,qty:1,entry:t.td.principal,exit:t.td.principal+a.netInt,pnl:a.netInt,pnlUSD,currency:cur,reason:'Matured',kind:'interest'});
  if (!t.exits) t.exits = []; // DI-05: guard for pre-exits schema trades
  t.exits.push({date:now(),price:t.td.principal+a.netInt,qty:1,reason:'Matured',pnl:a.netInt,currency:cur});
  t.status='Closed';t.closeDate=now();t.td.confirmed=true;t.td.confirmedAt=now();
  stampItem(t); // CRITICAL: bump _updatedAt so Supabase merge keeps local mutation
  lg('trade',t.ticker,`Matured — net ${f(a.netInt)} ${cur}`);
  save();renderAll();toast(t.ticker+' matured','a-g');
}

export async function delH(id){const h=S.holdings.find(x=>x.id===id);if(!h)return;if(!await uiConfirm('Remove '+h.ticker+' from holdings?',{title:'Remove holding',okText:'Remove',danger:true}))return;S.holdings=S.holdings.filter(x=>x.id!==id);tombstoneId(id);lg('holding',h.ticker,'Removed');save();renderP();}

// Bulk prices
export function openBulk(){const all=S.holdings.filter(h=>{const ps=PS[h.ticker];return(ps&&ps.s==='m')||!S.prices[h.ticker];});
document.getElementById('bpL').innerHTML=all.map(h=>{const cur=S.prices[h.ticker]?S.prices[h.ticker].price:'';
return`<div style="display:flex;align-items:center;gap:10px;margin-bottom:8px"><strong style="width:60px">${esc(h.ticker)}</strong>
<span style="width:100px;color:var(--t3)">${esc(h.subType)}</span>
<input class="price-input" style="width:120px" id="bp_${h.ticker}" value="${cur}" placeholder="Price (${h.currency})">
<span style="color:var(--t3);font-size:11px">${h.currency}</span></div>`;}).join('');
document.getElementById('moBP').classList.add('active');}

export function saveBP(){S.holdings.filter(h=>(PS[h.ticker]?.s==='m')||!S.prices[h.ticker]).forEach(h=>{
const el=document.getElementById('bp_'+h.ticker);if(el&&el.value){const p=parseFloat(el.value);
if(p>0){S.prices[h.ticker]={price:p,changePct:0,src:'manual',ts:Date.now(),cur:h.currency};lg('price',h.ticker,'Manual: '+p);}}});
save();clMo('moBP');renderAll();toast('Prices saved','a-b');}

// ============ PORTFOLIO MANAGEMENT ============

let editPortId = null;

/** Populate a <select> with active portfolios */
function _populatePortfolioSelect(selId, selectedVal) {
  const sel = document.getElementById(selId);
  if (!sel) return;
  const ports = (S.portfolios || []).filter(p => p.status === 'active');
  sel.innerHTML = ports.map(p =>
    `<option value="${p.id}" ${p.id === selectedVal ? 'selected' : ''}>${esc(p.name)}${p.isDefault ? ' (default)' : ''}</option>`
  ).join('');
}

/** Exported for monthly module */
export { _populatePortfolioSelect as populatePortfolioSelect };

/** Get active portfolio filter — null means "All" */
export function getActivePortfolio() {
  return S.activePortfolioId || null;
}

/** Get holdings filtered by active portfolio (null = all active portfolios) */
export function getPortfolioHoldings(portfolioId) {
  if (!portfolioId) return S.holdings;
  return S.holdings.filter(h => h.portfolioId === portfolioId);
}

/** Get default portfolio */
export function getDefaultPortfolio() {
  return (S.portfolios || []).find(p => p.isDefault) || (S.portfolios || [])[0];
}

/** Switch active portfolio filter */
export function switchPortfolio(id) {
  S.activePortfolioId = id || null;
  window._portBuilt = false; // rebuild neon donut/broker/allocation visuals for the new scope
  save();
  renderP();
}

/** Open create/edit portfolio modal */
export function openPortfolio(id) {
  editPortId = id || null;
  const el = (k) => document.getElementById(k);
  if (editPortId) {
    const p = (S.portfolios || []).find(x => x.id === editPortId);
    if (!p) return;
    el('moPortT').textContent = 'Edit Portfolio';
    el('portName').value = p.name;
    el('portType').value = p.type || 'custom';
    el('portHorizon').value = p.horizon || 'medium';
    el('portMandate').value = p.mandate || (p.isDefault ? 'accumulate' : 'active'); // mirrors alpha-score default
    el('portSizing').value = p.sizing || '';   // no inference: an undeclared sizing shows as unset
    el('portBase').value = p.base || '';
    el('portTags').value = (p.tags || []).join(', ');
    el('portNote').value = p.note || '';
    // Can't change default status or close from edit modal
    const closeBtn = el('portCloseBtn');
    if (closeBtn) closeBtn.style.display = p.isDefault ? 'none' : '';
  } else {
    el('moPortT').textContent = 'Create Portfolio';
    el('portName').value = '';
    el('portType').value = 'custom';
    el('portHorizon').value = 'medium';
    // No default. Mandate decides whether the AI may EVER recommend selling this sleeve, and a
    // pre-selected answer to that question is a guess wearing a decision's clothes. (2026-08-13)
    el('portMandate').value = '';
    el('portSizing').value = '';
    el('portBase').value = '';
    el('portTags').value = '';
    el('portNote').value = '';
    const closeBtn = el('portCloseBtn');
    if (closeBtn) closeBtn.style.display = 'none';
  }
  el('moPort').classList.add('active');
}

/** Save portfolio (create or update) */
export function savePortfolio() {
  const name = document.getElementById('portName').value.trim();
  if (!name) return toast('Portfolio name required', 'a-r');
  const type = document.getElementById('portType').value;
  const horizon = document.getElementById('portHorizon').value;
  const mandate = document.getElementById('portMandate')?.value;
  if (mandate !== 'active' && mandate !== 'accumulate') {
    return toast('Choose a mandate — it decides whether advisory may ever recommend an exit here', 'a-r');
  }
  const sizing = document.getElementById('portSizing')?.value;
  if (!['weighted', 'accumulate', 'cyclical'].includes(sizing)) {
    return toast('Choose a sizing — it decides how this sleeve gets its target, and nothing infers one', 'a-r');
  }
  const base = parseFloat(document.getElementById('portBase').value) || 0;
  const tagsStr = document.getElementById('portTags').value.trim();
  const tags = tagsStr ? tagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];
  const note = document.getElementById('portNote').value.trim();

  if (editPortId) {
    const p = (S.portfolios || []).find(x => x.id === editPortId);
    if (!p) return toast('Portfolio not found', 'a-r');
    p.name = name;
    p.type = type;
    p.horizon = horizon;
    p.mandate = mandate;
    p.sizing = sizing;
    p.base = base;
    p.tags = tags;
    p.note = note;
    stampItem(p);
    lg('portfolio', name, 'Updated portfolio');
  } else {
    if (!S.portfolios) S.portfolios = [];
    const np = {
      id: uid(),
      name,
      type,
      horizon,
      mandate,
      sizing,
      tags,
      isDefault: false,
      status: 'active',
      base,
      note,
      createdAt: now(),
      _updatedAt: Date.now()
    };
    S.portfolios.push(np);
    lg('portfolio', name, 'Created portfolio');
  }
  editPortId = null;
  save();
  clMo('moPort');
  renderP();
  toast(name + ' saved', 'a-b');
}

/** Close a portfolio — move holdings to cash or another portfolio */
export async function closePortfolio(id) {
  const p = (S.portfolios || []).find(x => x.id === id);
  if (!p) return;
  if (p.isDefault) return toast('Cannot close default portfolio', 'a-r');
  const holdings = S.holdings.filter(h => h.portfolioId === id);
  if (holdings.length > 0) {
    if (!await uiConfirm(`${holdings.length} holding(s) will be moved to the default portfolio.`, { title: `Close "${p.name}"?`, okText: 'Close portfolio' })) return;
    const def = getDefaultPortfolio();
    if (!def) return toast('No default portfolio found', 'a-r');
    holdings.forEach(h => { h.portfolioId = def.id; stampItem(h); });
  } else {
    if (!await uiConfirm(`Close "${p.name}"?`, { title: 'Close portfolio', okText: 'Close' })) return;
  }
  p.status = 'closed';
  p.closedAt = now();
  stampItem(p);
  // If active filter was this portfolio, reset to All
  if (S.activePortfolioId === id) S.activePortfolioId = null;
  lg('portfolio', p.name, 'Closed portfolio');
  save();
  renderP();
  toast(p.name + ' closed', 'a-b');
}

/** Close the portfolio currently being edited (from modal button) */
export function closeEditingPortfolio() {
  if (editPortId) { clMo('moPort'); closePortfolio(editPortId); }
}

/** Delete a closed portfolio permanently */
export async function deletePortfolio(id) {
  const p = (S.portfolios || []).find(x => x.id === id);
  if (!p) return;
  if (p.isDefault) return toast('Cannot delete default portfolio', 'a-r');
  if (p.status !== 'closed') return toast('Close the portfolio first', 'a-r');
  if (!await uiConfirm(`Permanently delete "${p.name}"? This cannot be undone.`, { title: 'Delete portfolio', okText: 'Delete', danger: true })) return;
  S.portfolios = S.portfolios.filter(x => x.id !== id);
  tombstoneId(id);
  lg('portfolio', p.name, 'Deleted portfolio');
  save();
  renderP();
  toast(p.name + ' deleted', 'a-b');
}

/** Render portfolio switcher bar */
export function renderPortfolioBar() {
  const bars = [document.getElementById('portfolioBar'), document.getElementById('portfolioBarNeo')].filter(Boolean);
  if (!bars.length) return;
  const active = S.activePortfolioId;
  const ports = (S.portfolios || []).filter(p => p.status === 'active');
  const hide = ports.length <= 1;
  const horizonColor = { short: 'var(--red)', medium: 'var(--yel)', long: 'var(--grn)' };
  // manage popover lists ALL portfolios (active + closed) so edit/close/delete are all reachable
  const allPorts = S.portfolios || [];
  const manage = `<details class="port-manage"><summary class="port-tab" title="Manage portfolios">⋯</summary>
    <div class="port-pop">
      <div class="pp-hdr">Manage portfolios</div>
      ${allPorts.map(p => {
        const hc = horizonColor[p.horizon] || 'var(--t3)';
        const closed = p.status === 'closed';
        const acts = closed
          ? `<button class="glassbtn" data-action="deletePortfolio" data-args='["${p.id}"]' style="color:#ff5470">Delete</button>`
          : `<button class="glassbtn" data-action="openPortfolio" data-args='["${p.id}"]'>Edit</button>${p.isDefault ? '' : ` <button class="glassbtn" data-action="closePortfolio" data-args='["${p.id}"]'>Close</button>`}`;
        return `<div class="pp-row"><span class="pp-name"><span class="port-dot" style="background:${closed ? 'var(--t3)' : hc}"></span>${esc(p.name)}${closed ? ' <span style="font-size:9px;color:var(--t3)">closed</span>' : ''}${p.isDefault ? ' <span style="font-size:9px;color:var(--t3)">default</span>' : ''}</span><span class="pp-acts">${acts}</span></div>`;
      }).join('')}
      <div class="pp-row"><button class="glassbtn port-add" data-action="openPortfolio" data-args='[null]' style="width:100%">+ New portfolio</button></div>
    </div></details>`;
  const html = hide ? '' : (`<button class="port-tab ${!active ? 'active' : ''}" data-action="switchPortfolio" data-args='[null]'>All</button>` +
    ports.map(p => {
      const isA = active === p.id;
      const hc = horizonColor[p.horizon] || 'var(--t3)';
      const cnt = S.holdings.filter(h => h.portfolioId === p.id).length;
      return `<button class="port-tab ${isA ? 'active' : ''}" data-action="switchPortfolio" data-args='["${p.id}"]'>
        <span class="port-dot" style="background:${hc}"></span>${esc(p.name)} <span style="font-size:10px;color:var(--t3)">(${cnt})</span>
      </button>`;
    }).join('') +
    `<button class="port-tab port-add" data-action="openPortfolio" data-args='[null]'>+ New</button>` +
    manage);
  bars.forEach(bar => { bar.style.display = hide ? 'none' : ''; bar.innerHTML = html; });
}

/** Render portfolio list in management card */
export function renderPortfolioList() {
  const el = document.getElementById('portfolioList');
  if (!el) return;
  // Show management card only when >1 portfolio exists
  const card = document.getElementById('portfolioMgmtCard');
  if (card) card.style.display = (S.portfolios || []).length > 0 ? '' : 'none';
  const ports = (S.portfolios || []);
  el.innerHTML = ports.map(p => {
    const cnt = S.holdings.filter(h => h.portfolioId === p.id).length;
    const hLabel = { short: 'Short', medium: 'Medium', long: 'Long' };
    const statusBadge = p.status === 'closed'
      ? '<span style="color:var(--red);font-weight:600;font-size:10px">CLOSED</span>'
      : '<span style="color:var(--grn);font-weight:600;font-size:10px">ACTIVE</span>';
    return `<div class="gls-soft" style="display:flex;align-items:center;justify-content:space-between;padding:8px 12px;border:1px solid var(--bd);border-radius:8px;margin-bottom:6px">
      <div>
        <strong>${esc(p.name)}</strong> ${p.isDefault ? '<span style="font-size:9px;color:var(--acc);border:1px solid var(--acc);padding:1px 4px;border-radius:3px">DEFAULT</span>' : ''}
        ${statusBadge}
        <div style="font-size:11px;color:var(--t3)">${hLabel[p.horizon] || '—'} term · ${cnt} holding(s) · Base: $${f(p.base || 0)}</div>
      </div>
      <div style="display:flex;gap:4px">
        ${p.status === 'active' ? `<button class="btn btn-s" data-action="openPortfolio" data-args='["${p.id}"]'>Edit</button>` : ''}
        ${!p.isDefault && p.status === 'active' ? `<button class="btn btn-s btn-d" data-action="closePortfolio" data-args='["${p.id}"]'>Close</button>` : ''}
        ${!p.isDefault && p.status === 'closed' ? `<button class="btn btn-s btn-d" data-action="deletePortfolio" data-args='["${p.id}"]'>Delete</button>` : ''}
      </div>
    </div>`;
  }).join('');
}

// ============ LOT-BASED OPERATIONS ============

/**
 * Open Exit Holding modal — sell shares from investment and/or DRIP lots
 */
/**
 * A HOLDING DOES NOT NEED LOTS TO BE SOLD.
 *
 * `Exit Holding` opened only when `h.lots` existed and had length, and execExitH bailed on
 * the same condition. Two of the owner's forty-four holdings have lots. The other forty-two
 * carry qty and avgCost — that is what the monthly plan writes, and blendedAvgCost already
 * says so out loud: "A holding can carry basis the lots do not describe — ASELS has TRY
 * 41,736 of averaged cost and no lots."
 *
 * So the button answered "No lots to exit" for every position the owner actually wanted to
 * close, and on 2026-08-04 that was all thirteen of the Option A migration sells. There was
 * no way to record a sale at all, and therefore no way for the app to ever learn that a
 * position had been exited: skipping a row in the monthly plan means "not bought this
 * month", never "sold".
 *
 * Averaged holdings sell against qty and avgCost. Under an average-cost basis a sale does
 * not change the average, so avgCost is left alone and only qty moves. The investment/DRIP
 * split is a property of lots and is simply absent where there are none. (audit 2026-08-04)
 */
function _hasLots(h) { return !!(h && Array.isArray(h.lots) && h.lots.length); }

export function openExitH(id) {
  const h = S.holdings.find(x => x.id === id);
  if (!h) return;
  if (!_hasLots(h)) return _openExitAveraged(h);
  const ls2 = lotSummary(h);
  if (!ls2) return;
  // Basis the lots do not describe. Zero for a position bought entirely through + Lot; on
  // 2026-08-14 it was non-zero for seven holdings, and it was unsellable through this dialog.
  const _tr = averagedTranche(h);
  // Every money figure below is in the currency the sale SETTLES in, which for a Cash holding
  // is lira, not the currency being sold. Printing "$44,342" over a lira number is how the
  // balance defect stayed invisible for as long as it did. (SPX-ACCT-01)
  const cur = settlementCurrency(h);
  const cSym = cur === 'TRY' ? '₺' : cur === 'EUR' ? '€' : '$';
  const _isCash = h.type === 'Cash';
  const p = S.prices[h.ticker];
  const curPrice = p ? p.price : 0;

  let moEl = document.getElementById('moExitH');
  if (!moEl) {
    moEl = document.createElement('div');
    moEl.id = 'moExitH';
    moEl.className = 'modal';
    moEl.innerHTML = '<div class="modal-content" style="max-width:480px"><div class="modal-header"><h3 id="moExitHT">Exit Holding</h3><span class="modal-close" data-action="clMoExitH">&times;</span></div><div id="moExitHBody"></div></div>';
    document.body.appendChild(moEl);
  }

  document.getElementById('moExitHT').textContent = 'Exit ' + h.ticker;
  document.getElementById('moExitHBody').innerHTML = `
    <div style="margin-bottom:12px;font-size:12px;color:var(--t2)">
      Current Price: <strong>${curPrice ? cSym + f(curPrice) : 'N/A'}</strong>
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px">
      <div style="background:var(--bg2);padding:10px;border-radius:8px;border-left:3px solid var(--acc)">
        <div style="font-size:10px;color:var(--t3)">Investment Shares</div>
        <div style="font-size:16px;font-weight:700">${f(ls2.investedQty, 4)}</div>
        <div style="font-size:10px;color:var(--t3)">Cost: ${cSym}${f(ls2.investedCost)}</div>
      </div>
      <div style="background:var(--bg2);padding:10px;border-radius:8px;border-left:3px solid var(--grn)">
        <div style="font-size:10px;color:var(--t3)">DRIP Shares</div>
        <div style="font-size:16px;font-weight:700">${f(ls2.dripQty, 4)}</div>
        <div style="font-size:10px;color:var(--t3)">Cost: ${cSym}${f(ls2.dripCost)}</div>
      </div>
      ${_tr.qty > 0 ? `<div style="background:var(--bg2);padding:10px;border-radius:8px;border-left:3px solid var(--ora)">
        <div style="font-size:10px;color:var(--t3)">Averaged Shares</div>
        <div style="font-size:16px;font-weight:700">${f(_tr.qty, 4)}</div>
        <div style="font-size:10px;color:var(--t3)">Cost: ${cSym}${f(_tr.cost)} · ${cSym}${f(_tr.unit, 4)}/share</div>
      </div>` : ''}
    </div>
    <div style="display:flex;gap:12px;margin-bottom:12px">
      <label style="flex:1;font-size:12px">Sell from Investment:
        <input id="exitInvQty" type="number" step="any" min="0" max="${ls2.investedQty}" value="0" class="price-input" style="width:100%;margin-top:4px">
      </label>
      <label style="flex:1;font-size:12px">Sell from DRIP:
        <input id="exitDripQty" type="number" step="any" min="0" max="${ls2.dripQty}" value="0" class="price-input" style="width:100%;margin-top:4px">
      </label>
      ${_tr.qty > 0 ? `<label style="flex:1;font-size:12px" title="Shares held before this holding had a lot list. Their basis is the average, not a dated purchase.">Sell from averaged:
        <input id="exitTrancheQty" type="number" step="any" min="0" max="${_tr.qty}" value="0" class="price-input" style="width:100%;margin-top:4px">
      </label>` : ''}
    </div>
    <label style="font-size:12px">${_isCash ? `Exit Rate (${cur} per ${h.currency})` : `Exit Price (${cur})`}:
      <input id="exitPrice" type="number" step="any" value="${curPrice || ''}" class="price-input" style="width:100%;margin-top:4px">
    </label>
    <div id="exitPreview" style="margin-top:12px;padding:10px;background:var(--bg2);border-radius:6px;font-size:11px;color:var(--t3)">Fill quantities to preview...</div>
    <div style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end">
      <button class="btn btn-s" data-action="clMoExitH">Cancel</button>
      <button class="btn btn-s" style="background:var(--red);color:#fff" id="exitHBtn" data-action="execExitH" data-args='["${h.id}"]'>Execute Exit</button>
    </div>`;

  const preview = () => {
    const invQ = parseFloat(document.getElementById('exitInvQty').value) || 0;
    const dripQ = parseFloat(document.getElementById('exitDripQty').value) || 0;
    const avgQ = parseFloat(document.getElementById('exitTrancheQty')?.value) || 0;
    const ep = parseFloat(document.getElementById('exitPrice').value) || 0;
    if (!ep || (invQ + dripQ + avgQ) <= 0) {
      document.getElementById('exitPreview').innerHTML = 'Fill quantities to preview...';
      return;
    }
    const totalQ = invQ + dripQ + avgQ;
    const proceeds = totalQ * ep;
    const invCostPer = ls2.investedQty > 0 ? ls2.investedCost / ls2.investedQty : 0;
    const dripCostPer = ls2.dripQty > 0 ? ls2.dripCost / ls2.dripQty : 0;
    const exitCost = invQ * invCostPer + dripQ * dripCostPer + avgQ * _tr.unit;
    const pnlVal = proceeds - exitCost;
    document.getElementById('exitPreview').innerHTML = `
      <div>Sell: <strong>${f(totalQ, 4)}</strong> shares (${f(invQ, 4)} inv + ${f(dripQ, 4)} drip${_tr.qty > 0 ? ' + ' + f(avgQ, 4) + ' avg' : ''})</div>
      <div>Proceeds: <strong>${cSym}${f(proceeds)}</strong></div>
      <div>Cost basis: ${cSym}${f(exitCost)}</div>
      <div class="${pnlVal >= 0 ? 'pos' : 'neg'}">P&L: ${cSym}${f(pnlVal)} (${exitCost > 0 ? f((pnlVal / exitCost) * 100, 1) : '0'}%)</div>
      <div style="margin-top:6px;color:var(--t3)">Remaining: ${f(ls2.investedQty - invQ, 4)} inv + ${f(ls2.dripQty - dripQ, 4)} drip${_tr.qty > 0 ? ' + ' + f(_tr.qty - avgQ, 4) + ' avg' : ''} = ${f((h.qty || 0) - totalQ, 4)} total</div>`;
  };
  moEl.classList.add('active');
  setTimeout(() => {
    document.getElementById('exitInvQty')?.addEventListener('input', preview);
    document.getElementById('exitDripQty')?.addEventListener('input', preview);
    document.getElementById('exitTrancheQty')?.addEventListener('input', preview);
    document.getElementById('exitPrice')?.addEventListener('input', preview);
  }, 50);
}

export function clMoExitH() { clMo('moExitH'); }

/**
 * Execute holding exit — deduct shares from lots (FIFO within each type),
 * create cashTx + realized record, update holding qty.
 */
export async function execExitH(id) {
  const h = S.holdings.find(x => x.id === id);
  if (!h) return;
  if (!_hasLots(h)) return _execExitAveraged(h);
  const invQ = parseFloat(document.getElementById('exitInvQty').value) || 0;
  const dripQ = parseFloat(document.getElementById('exitDripQty').value) || 0;
  // The lane that did not exist until 2026-08-14. Without it the dialog capped the sale at the
  // LOT quantity, so two thirds of TEJ could not be sold through the only exit this holding
  // routes to — the shares were unreachable as well as destructible.
  const tranche = averagedTranche(h);
  const avgQ = parseFloat(document.getElementById('exitTrancheQty')?.value) || 0;
  const exitPrice = parseFloat(document.getElementById('exitPrice').value) || 0;
  if ((invQ + dripQ + avgQ) <= 0) return toast('Enter quantity to sell', 'a-r');
  if (!exitPrice) return toast('Enter exit price', 'a-r');

  const ls2 = lotSummary(h);
  if (invQ > ls2.investedQty + 0.0001) return toast('Investment qty exceeds available', 'a-r');
  if (dripQ > ls2.dripQty + 0.0001) return toast('DRIP qty exceeds available', 'a-r');
  if (avgQ > tranche.qty + 0.0001) return toast('Averaged qty exceeds available', 'a-r');
  if (!await uiConfirm(`Exit ${f(invQ + dripQ + avgQ, 4)} shares of ${h.ticker} @ ${exitPrice}?`, { title: 'Exit holding', okText: 'Exit' })) return;

  // Deduct from lots (FIFO — oldest first)
  const deductFromLots = (type, qty) => {
    let remaining = qty;
    let totalCost = 0;
    const lotsOfType = h.lots.filter(l => l.type === type).sort((a, b) => a.date.localeCompare(b.date));
    for (const lot of lotsOfType) {
      if (remaining <= 0) break;
      const take = Math.min(lot.qty, remaining);
      const costPer = (lot.cost || lot.qty * lot.price) / lot.qty;
      totalCost += take * costPer;
      lot.qty -= take;
      lot.cost = lot.qty * costPer;
      remaining -= take;
    }
    h.lots = h.lots.filter(l => l.qty > 0.0001);
    return totalCost;
  };

  const invCost = invQ > 0 ? deductFromLots('investment', invQ) : 0;
  const dripCost = dripQ > 0 ? deductFromLots('drip', dripQ) : 0;
  // The averaged lane has no lot to deduct from; its cost per share is the residual basis
  // divided by the residual quantity, which is what `avgCost` already means for that part.
  const avgCostPer = tranche.qty > 0 ? tranche.unit : 0;
  const avgLaneCost = avgQ * avgCostPer;
  const totalExitQty = invQ + dripQ + avgQ;
  const totalCostBasis = invCost + dripCost + avgLaneCost;
  // The broker charges on the way out too. Booking proceeds at the quoted price overstated
  // realized P&L by the exit commission on every sale. (audit 2026-08-03)
  const sale = priceSale(h, totalExitQty, exitPrice);
  const proceeds = sale.proceedsNet;
  const pnlVal = proceeds - totalCostBasis;
  // From the currency the proceeds are actually IN. `toUSD(x, 'USD')` is the identity, so a
  // lira P&L on the USD cash holding was reported at ~47x. (SPX-ACCT-01)
  const pnlUSD = toUSD(pnlVal, settlementCurrency(h));

  // INCREMENTAL, never recomputed from the lot list — the inverse of what saveAddLot does on
  // the way in, and for the same reason it says there. Recomputing collapsed the position to
  // whatever the lots happened to describe: TEJ held 25,665.74 units against 7,539 in lots, so
  // selling the lot shares deleted 18,126.74 units and TRY 37,410 of basis — and selling ONE
  // share would have done the same, because the recompute does not care how much was sold.
  // Seven holdings were in that shape on 2026-08-14. (RET-001)
  {
    const after = holdingAfterExit(h.qty, h.avgCost, totalExitQty, totalCostBasis);
    h.qty = after.qty;
    if (h.qty > 0) h.avgCost = after.avgCost;
  }
  stampItem(h);

  // WHICH CURRENCY ACTUALLY ARRIVED — the mirror of saveAddLot's `payCur`, which has been
  // right since 2026-08-07 while both exit paths stayed wrong. For a Cash holding the proceeds
  // are LIRA, so crediting h.currency would have put TRY 44,342 into a USD 400 balance and
  // booked a lira P&L as dollars. (SPX-ACCT-01)
  const payCur = settlementCurrency(h);
  S.cash[payCur] = (S.cash[payCur] || 0) + proceeds;
  S.cashTxs.unshift(stampItem({
    id: uid(), date: now(), type: 'trade_exit',
    amount: proceeds, currency: payCur,
    note: `Exit ${h.ticker}: ${f(totalExitQty, 4)} shares @ ${exitPrice} (inv:${f(invQ, 4)} drip:${f(dripQ, 4)}${avgQ > 0 ? ` avg:${f(avgQ, 4)}` : ''}) P&L:${f(pnlVal)}`
  }));
  S.realized.push(stampItem({
    id: uid(), date: now(), ticker: h.ticker,
    qty: totalExitQty, entry: totalCostBasis / totalExitQty,
    // `exit` stays the QUOTED price, so the record still shows what the market paid; the
    // commission is a separate, auditable field and P&L is already net of it. Folding the
    // fee into `exit` would hide it inside a price and double-count it in any later report.
    exit: exitPrice, pnl: pnlVal, pnlUSD, currency: payCur,
    feeLocal: sale.feeLocal, feeModel: sale.feeModel,
    reason: 'Exit Holding'
  }));

  lg('holding', h.ticker, `Exit ${f(totalExitQty, 4)} @ ${exitPrice}`
    + (sale.feeLocal != null ? ` less ${f(sale.feeLocal, 2)} ${payCur} commission (${sale.feeModel})` : ` — commission unknown (${sale.feeModel}), proceeds booked gross`)
    + `, P&L: ${f(pnlVal)} ${payCur}`);
  save();
  clMo('moExitH');
  renderAll();
  toast(`${h.ticker}: ${f(totalExitQty, 4)} shares sold, P&L ${f(pnlVal)} ${payCur}`, pnlVal >= 0 ? 'a-g' : 'a-r');
}

/** Exit dialog for a holding whose basis is an average, not a lot list. */
function _openExitAveraged(h) {
  // As above: the settlement currency, not the currency being sold. (SPX-ACCT-01)
  const cur = settlementCurrency(h);
  const cSym = cur === 'TRY' ? '\u20ba' : cur === 'EUR' ? '\u20ac' : '$';
  const _isCash = h.type === 'Cash';
  const p = S.prices[h.ticker];
  const curPrice = p ? p.price : 0;
  const qty = h.qty || 0;
  if (!(qty > 0)) return toast('Nothing to exit \u2014 quantity is zero', 'a-r');

  let moEl = document.getElementById('moExitH');
  if (!moEl) {
    moEl = document.createElement('div');
    moEl.id = 'moExitH';
    moEl.className = 'modal';
    moEl.innerHTML = '<div class="modal-content" style="max-width:480px"><div class="modal-header"><h3 id="moExitHT">Exit Holding</h3><span class="modal-close" data-action="clMoExitH">&times;</span></div><div id="moExitHBody"></div></div>';
    document.body.appendChild(moEl);
  }
  document.getElementById('moExitHT').textContent = 'Exit ' + h.ticker;
  document.getElementById('moExitHBody').innerHTML = `
    <div style="margin-bottom:12px;font-size:12px;color:var(--t2)">
      Current Price: <strong>${curPrice ? cSym + f(curPrice) : 'N/A'}</strong>
      <span style="color:var(--t3)"> \u00b7 averaged basis, no lot history</span>
    </div>
    <div style="background:var(--bg2);padding:10px;border-radius:8px;border-left:3px solid var(--acc);margin-bottom:16px">
      <div style="font-size:10px;color:var(--t3)">Shares held</div>
      <div style="font-size:16px;font-weight:700">${f(qty, 4)}</div>
      <div style="font-size:10px;color:var(--t3)">Avg cost: ${cSym}${f(h.avgCost || 0, 4)} \u00b7 total ${cSym}${f(qty * (h.avgCost || 0))}</div>
    </div>
    <label style="font-size:12px">Shares to sell:
      <input id="exitAvgQty" type="number" step="any" min="0" max="${qty}" value="${qty}" class="price-input" style="width:100%;margin-top:4px">
    </label>
    <div style="margin-top:6px"><button class="btn btn-s" data-action="exitAvgAll" data-args='["${h.id}"]' style="font-size:10px;padding:2px 8px">Sell all ${f(qty, 4)}</button></div>
    <label style="font-size:12px;display:block;margin-top:12px">${_isCash ? `Exit Rate (${cur} per ${h.currency})` : `Exit Price (${cur})`}:
      <input id="exitPrice" type="number" step="any" value="${curPrice || ''}" class="price-input" style="width:100%;margin-top:4px">
    </label>
    <div id="exitPreview" style="margin-top:12px;padding:10px;background:var(--bg2);border-radius:6px;font-size:11px;color:var(--t3)">Fill quantity and price to preview...</div>
    <div style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end">
      <button class="btn btn-s" data-action="clMoExitH">Cancel</button>
      <button class="btn btn-s" style="background:var(--red);color:#fff" data-action="execExitH" data-args='["${h.id}"]'>Execute Exit</button>
    </div>`;

  const preview = () => {
    const q = parseFloat(document.getElementById('exitAvgQty').value) || 0;
    const ep = parseFloat(document.getElementById('exitPrice').value) || 0;
    const el = document.getElementById('exitPreview');
    if (!el) return;
    if (!ep || q <= 0) { el.textContent = 'Fill quantity and price to preview...'; return; }
    const sale = priceSale(h, q, ep);
    const basis = q * (h.avgCost || 0);
    const pnl = sale.proceedsNet - basis;
    el.innerHTML = `Proceeds <strong>${cSym}${f(sale.proceedsNet)}</strong>`
      + (sale.feeLocal != null ? ` (after ${cSym}${f(sale.feeLocal, 2)} commission, ${sale.feeModel})`
        : ` \u2014 <span style="color:var(--yel)">commission unknown (${sale.feeModel}), booked gross</span>`)
      + ` \u00b7 basis ${cSym}${f(basis)} \u00b7 P&L <strong style="color:${pnl >= 0 ? 'var(--grn)' : 'var(--red)'}">${cSym}${f(pnl)}</strong>`
      + ` \u00b7 remaining ${f((h.qty || 0) - q, 4)} shares`;
  };
  ['exitAvgQty', 'exitPrice'].forEach(idd => {
    const el = document.getElementById(idd);
    if (el) el.addEventListener('input', preview);
  });
  preview();
  document.getElementById('moExitH').classList.add('active');
}

/** Fill the quantity box with the whole position. */
export function exitAvgAll(id) {
  const h = S.holdings.find(x => x.id === id);
  const el = document.getElementById('exitAvgQty');
  if (!h || !el) return;
  el.value = h.qty || 0;
  el.dispatchEvent(new Event('input'));
}

async function _execExitAveraged(h) {
  const qty = parseFloat(document.getElementById('exitAvgQty')?.value) || 0;
  const exitPrice = parseFloat(document.getElementById('exitPrice')?.value) || 0;
  if (qty <= 0) return toast('Enter quantity to sell', 'a-r');
  if (!exitPrice) return toast('Enter exit price', 'a-r');
  if (qty > (h.qty || 0) + 0.0001) return toast('Quantity exceeds the position', 'a-r');
  if (!await uiConfirm(`Exit ${f(qty, 4)} shares of ${h.ticker} @ ${exitPrice}?`, { title: 'Exit holding', okText: 'Exit' })) return;

  // Same rule as the lot path and as saveAddLot: for a Cash holding the money that moves is
  // LIRA, because `qty` is the amount and `avgCost` is the rate. The EUR cash holding routes
  // here (no lots) and the USD one routes to the lot path — one defect, one on each. (ACCT-01)
  const cur = settlementCurrency(h);
  const avg = h.avgCost || 0;
  const totalCostBasis = qty * avg;
  const sale = priceSale(h, qty, exitPrice);
  const proceeds = sale.proceedsNet;
  const pnlVal = proceeds - totalCostBasis;
  const pnlUSD = toUSD(pnlVal, cur);

  // Average-cost basis: a sale does not move the average. Only the quantity changes, and a
  // full exit leaves avgCost standing so the row keeps its history rather than reading as a
  // position that cost nothing.
  h.qty = Math.max(0, (h.qty || 0) - qty);
  stampItem(h);

  S.cash[cur] = (S.cash[cur] || 0) + proceeds;
  S.cashTxs.unshift(stampItem({
    id: uid(), date: now(), type: 'trade_exit',
    amount: proceeds, currency: cur,
    note: `Exit ${h.ticker}: ${f(qty, 4)} shares @ ${exitPrice} (averaged basis) P&L:${f(pnlVal)}`
  }));
  S.realized.push(stampItem({
    id: uid(), date: now(), ticker: h.ticker,
    qty, entry: avg, exit: exitPrice, pnl: pnlVal, pnlUSD, currency: cur,
    feeLocal: sale.feeLocal, feeModel: sale.feeModel,
    reason: 'Exit Holding'
  }));
  lg('holding', h.ticker, `Exit ${f(qty, 4)} @ ${exitPrice}`
    + (sale.feeLocal != null ? ` less ${f(sale.feeLocal, 2)} ${cur} commission (${sale.feeModel})` : ` — commission unknown (${sale.feeModel}), proceeds booked gross`)
    + `, P&L: ${f(pnlVal)} ${cur}`
    + (h.qty <= 0.0001 ? ' — position closed' : ` — ${f(h.qty, 4)} left`));
  save();
  clMo('moExitH');
  renderAll();
  toast(`${h.ticker}: ${f(qty, 4)} shares sold, P&L ${f(pnlVal)} ${cur}`, pnlVal >= 0 ? 'a-g' : 'a-r');
}


/**
 * Open Add Lot modal — add new investment or DRIP lot to an existing holding
 */
export function openAddLot(id) {
  const h = S.holdings.find(x => x.id === id);
  if (!h) return;
  const cur = h.currency || 'EUR';

  let moEl = document.getElementById('moAddLot');
  if (!moEl) {
    moEl = document.createElement('div');
    moEl.id = 'moAddLot';
    moEl.className = 'modal';
    moEl.innerHTML = '<div class="modal-content" style="max-width:400px"><div class="modal-header"><h3 id="moAddLotT">Add Lot</h3><span class="modal-close" data-action="clMoAddLot">&times;</span></div><div id="moAddLotBody"></div></div>';
    document.body.appendChild(moEl);
  }

  document.getElementById('moAddLotT').textContent = 'Add Lot — ' + h.ticker;
  document.getElementById('moAddLotBody').innerHTML = `
    <label style="font-size:12px">Date:
      <input id="lotDate" type="date" value="${new Date().toISOString().slice(0, 10)}" class="price-input" style="width:100%;margin-top:4px">
    </label>
    <label style="font-size:12px;margin-top:8px;display:block">Type:
      <select id="lotType" class="price-input" style="width:100%;margin-top:4px">
        <option value="investment">Investment</option>
        <option value="drip">DRIP (Dividend)</option>
      </select>
    </label>
    <label style="font-size:12px;margin-top:8px;display:block">Quantity:
      <input id="lotQty" type="number" step="any" class="price-input" style="width:100%;margin-top:4px">
    </label>
    <label style="font-size:12px;margin-top:8px;display:block">Price (${cur}):
      <input id="lotPrice" type="number" step="any" class="price-input" style="width:100%;margin-top:4px">
    </label>
    <label style="font-size:12px;margin-top:8px;display:block">Cost (${cur}) — auto-calculated:
      <input id="lotCost" type="number" step="any" class="price-input" style="width:100%;margin-top:4px" readonly>
    </label>
    <div style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end">
      <button class="btn btn-s" data-action="clMoAddLot">Cancel</button>
      <button class="btn btn-s" style="background:var(--acc);color:#fff" data-action="saveAddLot" data-args='["${h.id}"]'>Add Lot</button>
    </div>`;

  moEl.classList.add('active');
  setTimeout(() => {
    const calc = () => {
      const q = parseFloat(document.getElementById('lotQty').value) || 0;
      const p2 = parseFloat(document.getElementById('lotPrice').value) || 0;
      document.getElementById('lotCost').value = (q * p2).toFixed(2);
    };
    document.getElementById('lotQty')?.addEventListener('input', calc);
    document.getElementById('lotPrice')?.addEventListener('input', calc);
  }, 50);
}

export function clMoAddLot() { clMo('moAddLot'); }

export function saveAddLot(id) {
  const h = S.holdings.find(x => x.id === id);
  if (!h) return;
  const date = document.getElementById('lotDate').value;
  const type = document.getElementById('lotType').value;
  const qty = parseFloat(document.getElementById('lotQty').value);
  const price = parseFloat(document.getElementById('lotPrice').value);
  if (!date || !qty || !price) return toast('Fill all fields', 'a-r');
  // A CASH HOLDING CANNOT PAY A DIVIDEND, SO IT CANNOT HAVE A DRIP.
  //
  // On a Cash row `qty` IS the currency amount and `price` IS the FX rate, a convention this
  // file, monthly.js and hVal all rely on. A drip entered here therefore adds currency to the
  // balance and blends a SHARE PRICE into an exchange-rate average.
  //
  // Measured 2026-09-10: the July and August SAP dividends landed on the EUR row instead of
  // SAP. EUR gained 4.637729 of quantity it never received and its average rate went from
  // 51.043 to 51.745, because 171.679 and 186.666 were averaged in as if they were lira per
  // euro. Two false `S.dividends` rows were written under ticker EUR as well. The app accepted
  // all of it without a word. (audit 2026-09-10)
  if (type === 'drip' && h.type === 'Cash') {
    return toast(`${h.ticker} is a cash row — its price is an FX rate, not a share price. A DRIP belongs on the holding that paid the dividend.`, 'a-r');
  }
  const cur = h.currency || 'EUR';

  // A purchase costs what the broker charges for it. monthly.js has always priced its buys
  // through pricePurchase — commission inside the per-share price — while this path stored the
  // quoted price bare, so the same instrument bought two ways carried two different cost bases.
  // DRIP is exempt: a reinvested distribution is not a brokered order.
  const buy = type === 'drip' ? null : pricePurchase(h, qty, price);
  const lotPrice = buy ? buy.price : price;
  const cost = qty * lotPrice;

  // BLEND, never recompute from the lot list. A holding can carry basis the lots do not
  // describe — ASELS has TRY 41,736 of averaged cost and no lots — and recomputing would
  // delete it. Incremental is right in both shapes. (audit 2026-08-03)
  const _prevQty = h.qty || 0;
  h.avgCost = blendedAvgCost(_prevQty, h.avgCost, { qty, price: lotPrice, cost, type });
  if (!h.lots) h.lots = [];
  // `funded: 'cash'` marks the lots whose money is already visible to the cash ledger below,
  // so capital.js's plan_lots door does not count them as capital arriving from outside.
  // `funded: 'cash'` marks a lot whose money the cash ledger has already seen, so capital.js's
  // plan_lots door does not count it as capital arriving from outside. A payroll-funded lot has
  // NOT been seen by the ledger and IS capital arriving, so it must carry no stamp -- which is
  // exactly the shape the eleven older SAP lots already have. See `fundsFromCashLedger`.
  const _viaCash = fundsFromCashLedger(h);
  h.lots.push({ id: uid(), date, type, qty, price: lotPrice, cost, ...(type !== 'drip' && _viaCash ? { funded: 'cash' } : {}) });
  h.qty = _prevQty + qty;
  stampItem(h);

  // _execExitAveraged credits cash on a sale. This path credited nothing on a purchase, so the
  // shares appeared while the money that bought them stayed put. Seven lots entered on
  // 2026-08-06 left USD 6,338.17 of sale proceeds counted twice — once as cash, once as ETFs.
  // The two legs have to move together or net worth is fiction.
  if (type !== 'drip' && _viaCash) {
    // WHICH CURRENCY ACTUALLY LEFT.
    //
    // For every other holding `cost` is qty * price in the holding's own currency, so the
    // holding's currency is also the currency spent. A Cash holding is the exception and the
    // exception is total: `qty` is the AMOUNT (876.81 USD) and `price` is the FX RATE (47.65
    // TRY per USD), so `cost` is denominated in the currency that was HANDED OVER — lira.
    // Debiting h.currency would have taken TRY 2,439 out of the dollar balance as if it were
    // 2,439 dollars, on a balance of 400. Caught before the row was ever recorded, on the
    // Garanti USD purchase of 2026-08-07. (audit 2026-08-07)
    //
    // 'TRY' is not a guess: every cash holding in this book quotes its rate as lira per unit
    // (usdtry, eurtry), which is the same convention monthly.js and hVal already rely on.
    const payCur = settlementCurrency(h);
    S.cash[payCur] = (S.cash[payCur] || 0) - cost;
    // MOVE-002: AN INVERSE CANNOT KNOW WHAT TO UNDO UNLESS THE FORWARD PATH WROTE IT DOWN.
    // Nothing linked a lot to the cash row it wrote, so `delLot` would have had to find it by
    // date + amount + note -- inference over prose, which is the one thing capital.js spends
    // forty lines refusing to do. The id goes on the lot instead, and a lot without it is
    // refused rather than guessed at.
    const _buyTxId = uid();
    h.lots[h.lots.length - 1].cashTxId = _buyTxId;
    S.cashTxs.unshift(stampItem({
      id: _buyTxId, date: date + 'T12:00:00', type: 'portfolio_buy',
      amount: -cost, currency: payCur,
      note: `Buy ${h.ticker}: ${f(qty, 4)} @ ${buy.grossPrice}`
        + (buy.feeLocal != null
          ? ` + ${f(buy.feeLocal, 2)} ${payCur} commission (${buy.feeModel})`
          : ` — commission unknown (${buy.feeModel}), cost booked gross`)
    }));
    if (S.cash[payCur] < 0) toast(`${payCur} balance is now ${f(S.cash[payCur], 2)} — record the funding leg`, 'a-w');
  }

  // If DRIP, also create dividend record
  if (type === 'drip') {
    if (!S.dividends) S.dividends = [];
    S.dividends.push(stampItem({
      id: uid(), date, ticker: h.ticker,
      gross: cost, currency: cur,
      whtPct: 0, whtAmt: 0, net: cost,
      note: `${h.ticker} DRIP — ${f(qty, 4)} shares @ ${price}`,
      drip: true
    }));
    S.cashTxs.unshift(stampItem({ id: uid(), date: date + 'T12:00:00', type: 'deposit', amount: cost, currency: cur, note: `Dividend: ${h.ticker} (DRIP)` }));
    S.cashTxs.unshift(stampItem({ id: uid(), date: date + 'T12:01:00', type: 'withdraw', amount: -cost, currency: cur, note: `DRIP reinvest: ${h.ticker}` }));
  }

  lg('holding', h.ticker, `Added ${type} lot: ${f(qty, 4)} @ ${price} (${f(cost)} ${cur})`);
  save();
  clMo('moAddLot');
  renderAll();
  toast(`${h.ticker}: ${type} lot added (${f(qty, 4)} shares)`, 'a-g');
}

/**
 * THE INVERSE OF `saveAddLot`, AND THE REASON IT EXISTS.
 *
 * There was none. A mistyped or duplicated lot had exactly one remedy -- a schema migration --
 * and that bill was paid three times in three weeks: v56 (two SAP lots stamped `funded: 'cash'`
 * that moved no cash), v57 (six Garanti rows duplicated and cross-contaminated), v59 (one DRIP
 * priced a lira low). Three schema versions and three deploys for what should be a button.
 *
 * Every leg `saveAddLot` writes is reversed here, together, because that is how they were
 * written:
 *
 *   avgCost   `holdingAfterExit`, which math.js names as blendedAvgCost's inverse. A DRIP is
 *             removed at exitCost 0 -- it was ADDED at zero cost, so taking it out removes no
 *             basis and the average RISES. v56 and v59 both record getting this backwards first.
 *   qty       minus the lot's own quantity.
 *   cash      credited back, and only when the lot says `funded: 'cash'`. A payroll lot moved no
 *             cash and must not move any now.
 *   cashTx    `softDelete` -- splice AND tombstone. An untombstoned delete resurrects on the
 *             next merge and re-spends the money.
 *   month     a month that loses its last lot loses its `source:'lots'` entry, or the plan
 *             renders `✓ Recorded` for a purchase that no longer exists. With lots remaining the
 *             entry is rebuilt from what is left.
 *
 * ⚠ A CASH-FUNDED LOT WITH NO `cashTxId` IS REFUSED. Lots written before that field existed
 * cannot be unwound without guessing which of two identical rows to delete, and that is how
 * money disappears. The refusal names the row it could not identify and writes nothing.
 *
 * ⚠ AND IT NEVER RUNS UNATTENDED. The third defect of the reconciler-pump audit was that a
 * money-moving repair ran on every load; this is a UI action behind a confirmation that states
 * every leg before it touches anything.
 */
/**
 * THE LIST THE DETAIL ROW NEVER SHOWED.
 *
 * `_holdingDetailRow` renders aggregates -- investment qty/cost, DRIP qty/cost, "N lots" -- so
 * there has never been anywhere to click an individual lot. That is the other half of why a
 * mistyped lot needed a migration: not only was there no inverse, there was no way to point at
 * the row you meant.
 */
export function openLots(id) {
  const h = S.holdings.find(x => x && x.id === id);
  if (!h) return;
  const cur = h.currency || 'EUR';
  let el = document.getElementById('moLots');
  if (!el) {
    el = document.createElement('div');
    el.id = 'moLots';
    el.className = 'modal';
    el.innerHTML = '<div class="modal-content" style="max-width:620px"><div class="modal-header">'
      + '<h3 id="moLotsT">Lots</h3><span class="modal-close" data-action="clMoLots">&times;</span></div>'
      + '<div id="moLotsBody"></div></div>';
    document.body.appendChild(el);
  }
  document.getElementById('moLotsT').textContent = 'Lots — ' + h.ticker;
  const lots = (h.lots || []).slice().sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  const rows = lots.length ? lots.map(l => {
    const c = Number(l.cost) || (Number(l.qty) || 0) * (Number(l.price) || 0);
    const drip = l.type === 'drip';
    // An unlinked cash-funded lot is marked HERE too, so the refusal is not a surprise at the
    // moment of clicking.
    const unlinked = !drip && l.funded === 'cash' && !l.cashTxId;
    return `<tr><td style="font-size:11px">${esc(String(l.date || ''))}</td>
      <td><span class="badge gls-soft" style="font-size:9px;color:${drip ? 'var(--grn)' : 'var(--acc)'}">${drip ? 'DRIP' : 'INV'}</span></td>
      <td class="tr" style="font-size:11px">${f(l.qty, 6)}</td>
      <td class="tr" style="font-size:11px">${f(l.price, 6)}</td>
      <td class="tr" style="font-size:11px">${fByCur(cur, c)}</td>
      <td style="font-size:10px;color:var(--t3)">${drip ? 'no cash' : l.funded === 'cash' ? (unlinked ? '<span style="color:var(--yel)">cash · unlinked</span>' : 'cash') : 'external'}</td>
      <td><button class="btn btn-s btn-d" style="font-size:9px" data-action="delLot" data-args='["${h.id}","${l.id}"]'>Delete</button></td></tr>`;
  }).join('') : '<tr><td colspan="7" style="color:var(--t3);font-size:11px">No lots recorded.</td></tr>';
  document.getElementById('moLotsBody').innerHTML = `
    <div style="max-height:52vh;overflow:auto"><table style="width:100%">
      <thead><tr style="font-size:10px;color:var(--t3)"><th>Date</th><th>Type</th><th class="tr">Qty</th><th class="tr">Price</th><th class="tr">Cost</th><th>Funded</th><th></th></tr></thead>
      <tbody>${rows}</tbody></table></div>
    <div style="margin-top:10px;font-size:10px;color:var(--t3)">
      Deleting a lot reverses its quantity, its average and the cash it moved, together. A lot
      recorded before purchases were linked to their cash row is refused rather than guessed at.
    </div>
    <div style="margin-top:12px;display:flex;gap:8px;justify-content:flex-end">
      <button class="btn btn-s" data-action="clMoLots">Close</button>
      <button class="btn btn-s" style="background:var(--acc);color:#fff" data-action="openAddLot" data-args='["${h.id}"]'>+ Lot</button>
    </div>`;
  el.classList.add('active');
}

export function clMoLots() { clMo('moLots'); }

export async function delLot(id, lotId) {
  const h = S.holdings.find(x => x && x.id === id);
  if (!h) return;
  const lot = (h.lots || []).find(l => l && l.id === lotId);
  if (!lot) return toast('That lot is already gone', 'a-w');

  const cur = h.currency || 'EUR';
  const isDrip = lot.type === 'drip';
  const cost = Number(lot.cost) || (Number(lot.qty) || 0) * (Number(lot.price) || 0);
  const payCur = settlementCurrency(h);
  const viaCash = !isDrip && lot.funded === 'cash';

  if (viaCash && !lot.cashTxId) {
    return toast(`${h.ticker}: this lot was recorded before purchases were linked to their cash row. `
      + `Its ${fByCur(payCur, cost)} ${payCur} debit cannot be identified without guessing, so nothing was changed.`, 'a-r');
  }

  const after = holdingAfterExit(h.qty, h.avgCost, lot.qty, isDrip ? 0 : cost);
  const mo = String(lot.date || '').slice(0, 7);
  const monthEntry = /^\d{4}-\d{2}$/.test(mo) && S.monthlyRates && S.monthlyRates[mo]
    && (S.monthlyRates[mo].entries || {})[h.ticker];
  const remainingInMonth = monthLots(h, mo).filter(l => l.id !== lotId).length;

  const lines = [
    `${h.ticker} — remove ${isDrip ? 'DRIP' : 'investment'} lot of ${f(lot.qty, 6)} @ ${f(lot.price, 6)} (${lot.date})`,
    '',
    `Quantity  ${f(h.qty, 6)} → ${f(after.qty, 6)}`,
    `Avg cost  ${f(h.avgCost, 6)} → ${f(after.avgCost, 6)}${isDrip ? '  (rises — a DRIP carries no cost)' : ''}`,
  ];
  if (viaCash) lines.push(`Cash      ${payCur} ${f(S.cash[payCur] || 0, 2)} → ${f((S.cash[payCur] || 0) + cost, 2)}`);
  else lines.push('Cash      unchanged — this lot moved none');
  if (isDrip) lines.push('Dividend  the matching record and its two ledger legs go with it');
  if (monthEntry) lines.push(`Month     ${mo} entry ${remainingInMonth ? 'rebuilt from the remaining lots' : 'removed'}`);

  if (!await uiConfirm(lines.join('\n'), { title: 'Delete lot', okText: 'Delete lot', danger: true })) return;

  h.lots = (h.lots || []).filter(l => l !== lot);
  h.qty = after.qty;
  if (after.qty > 0 || !isDrip) h.avgCost = after.avgCost;
  stampItem(h);

  if (viaCash) {
    S.cash[payCur] = (S.cash[payCur] || 0) + cost;
    softDelete('cashTxs', lot.cashTxId);
  }

  if (isDrip) {
    const d = (S.dividends || []).find(x => x && x.ticker === h.ticker && x.drip
      && String(x.date || '') === String(lot.date || '') && Math.abs((Number(x.gross) || 0) - cost) < 0.01);
    if (d) softDelete('dividends', d.id);
    // Its two legs net to zero, so cash does not move -- but a row left behind is a false record.
    (S.cashTxs || []).filter(t => t && String(t.date || '').slice(0, 10) === String(lot.date || '').slice(0, 10)
      && (t.currency || '') === cur && Math.abs(Math.abs(Number(t.amount) || 0) - cost) < 0.01
      && (new RegExp(`^Dividend: ${h.ticker}`).test(t.note || '') || new RegExp(`^DRIP reinvest: ${h.ticker}`).test(t.note || ''))
    ).slice(0, 2).forEach(t => softDelete('cashTxs', t.id));
  }

  if (monthEntry) {
    const r = S.monthlyRates[mo];
    if (!remainingInMonth) delete r.entries[h.ticker];
    else {
      const le = lotEntry(h, mo);
      if (le) r.entries[h.ticker] = { qty: le.qty, price: le.price, date: now(), source: 'lots', lotIds: le.lotIds };
      else delete r.entries[h.ticker];
    }
  }

  lg('holding', h.ticker, `Deleted ${isDrip ? 'drip' : 'investment'} lot: ${f(lot.qty, 4)} @ ${f(lot.price, 6)}`
    + (viaCash ? ` — ${fByCur(payCur, cost)} returned to ${payCur}` : ' — moved no cash'));
  save();
  renderAll();
  toast(`${h.ticker}: lot removed`, 'a-g');
}
